import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { cifra, useLang } from '../i18n.jsx';
import Dropdown from './Dropdown.jsx';
import { cardArt } from '../cardArt.js';
import DeckSheet from './DeckSheet.jsx';
import TypeIcon from './TypeIcon.jsx';
import { retraso, useMedia } from '../movimiento.js';
import { api } from '../api.js';
import { claveCache, guardarCache, leerCache } from '../cache.js';
import { useEstadoUrl } from '../estado-url.js';
import { useAuth } from '../auth.jsx';
import ProGate from './ProGate.jsx';
import PlayerCard from './PlayerCard.jsx';
import { Candado, Cargando, Fallo, Vacio } from './Decorado.jsx';
import Paginador from './Paginador.jsx';
import Bandera from './Bandera.jsx';
import Subtabs from './Subtabs.jsx';
import Pista from './Pista.jsx';
import Nucleo, { Mini } from './NucleoSprites.jsx';

/**
 * Vista del metajuego.
 *
 * La idea rectora: presencia y resultados por separado, porque el hueco entre
 * ambos es la informacion util. Un mazo muy jugado con victorias mediocres
 * significa que hay sitio para atacarlo; uno poco jugado que gana mucho es un
 * candidato a romper el meta. Por eso la tabla se puede ordenar por cualquiera
 * de las dos y se marca el diferencial.
 */

const pct = (n, digits = 1) => (n == null ? '—' : `${(n * 100).toFixed(digits)}%`);

/**
 * Cifra que CUENTA hasta su valor en vez de aparecer puesta.
 *
 * Es el gesto que dice "esto esta vivo": el numero sube delante de ti al entrar
 * y cada vez que el sondeo trae un valor nuevo, en lugar de cambiar en seco
 * como un rotulo estatico. La curva frena al final (cubica), que es lo que hace
 * legible el aterrizaje en el valor real.
 *
 * Con menos movimiento pedido, el valor se pone directo: contar ES movimiento.
 */
function useConteo(valor, dur = 1100) {
  const [v, setV] = useState(valor);
  const prev = useRef(null);
  useEffect(() => {
    const desde = prev.current ?? 0;
    prev.current = valor;
    if (
      desde === valor ||
      (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      setV(valor);
      return undefined;
    }
    let raf;
    const t0 = performance.now();
    const paso = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const suave = 1 - (1 - p) ** 3;
      setV(Math.round(desde + (valor - desde) * suave));
      if (p < 1) raf = requestAnimationFrame(paso);
    };
    raf = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(raf);
  }, [valor, dur]);
  return v;
}

/* Paises de Latinoamerica, para la region por defecto de "Proximos". Misma
   lista que la del servidor (proximos.js); aqui se filtra en el cliente porque
   son 140 filas y cambiar de region no debe costar un viaje. */
const LATAM_PAISES = ['CL', 'AR', 'BR', 'MX', 'PE', 'CO', 'UY', 'EC', 'BO', 'PY', 'VE', 'CR', 'PA', 'GT'];

/* La fecha llega en dos formas: `2027-02-27` del calendario oficial y
   `2026-08-01T12:00:00.000Z` de los de comunidad. Se corta a diez y se ancla a
   mediodia, como en el resto de la casa, para que la zona horaria no reste un
   dia al pintarla. */
const fechaDe = (f) => new Date(`${String(f).slice(0, 10)}T12:00:00`);
const diaDe = (f) => String(fechaDe(f).getDate());
const mesDe = (f, lang) =>
  fechaDe(f).toLocaleDateString(lang === 'es' ? 'es-CL' : 'en-US', { month: 'short' }).replace('.', '');

function CifraViva({ n, lang }) {
  const v = useConteo(n ?? 0);
  const destino = cifra(n, lang);
  /**
   * RESERVA EL ANCHO DEL NUMERO FINAL desde el primer fotograma.
   *
   * La cuenta va de 0 a 17.588, o sea de UN caracter a SEIS, y el ancho del
   * texto crecia con ella. En el telefono, donde estas cifras van en su propia
   * linea, ese crecimiento hacia reflotar la linea a mitad de la animacion y
   * empujaba hacia abajo los desplegables de formato y periodo: medido en
   * produccion, era la mayor fuente de saltos de maquetacion de la portada
   * (`.meta__controls` oscilando 343→314→343 en treinta milisegundos).
   *
   * El ancho lo marca el NUMERO FINAL escrito de verdad y escondido debajo,
   * no un calculo en `ch`: con separador de miles ("17.588") el punto no mide
   * lo mismo que un digito ni con cifras tabulares, asi que la cuenta en `ch`
   * se pasaba de ancho y dejaba un hueco permanente antes de la palabra. El
   * medidor mide exacto por definicion. La animacion se ve igual; lo unico que
   * cambia es que ya no mueve nada a su alrededor.
   */
  return (
    <strong className="cifraviva">
      <span className="cifraviva__medida" aria-hidden="true">{destino}</span>
      {/* MISMO formateo que el medidor de arriba, sin excepcion: si uno agrupa
          los miles y el otro no, el ancho reservado deja de ser el del valor
          final y vuelve el salto de maquetacion que este medidor existe para
          evitar. */}
      <span className="cifraviva__valor">{cifra(v, lang)}</span>
    </strong>
  );
}

/** "hace 12 min" / "hace 4 h" a partir de la edad en milisegundos. */
const haceCuanto = (ageMs, t) => {
  const min = Math.max(1, Math.round(ageMs / 60000));
  return min < 90 ? t('haceMin', { n: min }) : t('haceHoras', { n: Math.round(min / 60) });
};

/**
 * "Infravalorado": victorias por encima del 50 % menos la presencia.
 *
 * Penaliza la popularidad a proposito. Un mazo al 55 % que juega el 2 % del
 * campo sale muy por delante de otro al 55 % que juega el 20 %: el segundo ya
 * esta descubierto y el campo se prepara contra el, mientras que el primero es
 * por donde se rompe un meta.
 */
const edgeScore = (a) => (a.winRate ?? 0.5) - 0.5 - a.share;

/**
 * Columnas de la tabla.
 *
 * Se declaran como dato y no como JSX repetido porque cabecera, celda y criterio
 * de orden tienen que casar: teniendolos juntos, añadir una columna es añadir
 * una entrada y no tocar tres sitios que se pueden desincronizar.
 *
 * `desc: true` significa que el primer clic ordena de mayor a menor, que es lo
 * que uno quiere de una columna numerica -- pinchas "Victorias" para ver quien
 * gana MAS, no quien gana menos. En la de nombre es al reves.
 */
const COLUMNS = [
  { key: 'name', labelKey: 'archetype', align: 'left', desc: false, value: (a) => a.name },
  /*
   * Tipo predominante como columna PROPIA, no pegado al nombre: alineados en su
   * columna los simbolos se comparan de un vistazo, y ordenar por ella agrupa
   * los mazos del mismo tipo. El valor de orden es el tipo principal (el de
   * mayor cuota); los sin tipo van al final.
   */
  { key: 'type', labelKey: 'type', align: 'left', desc: false, value: (a) => a.types?.[0]?.key ?? '~' },
  { key: 'share', labelKey: 'share', align: 'num', desc: true, value: (a) => a.share },
  { key: 'winRate', labelKey: 'winRateShort', align: 'num', desc: true, value: (a) => a.winRate ?? -1 },
  { key: 'edge', labelKey: 'edge', align: 'num', desc: true, hintKey: 'edgeHint', value: edgeScore },
  {
    key: 'conversion',
    labelKey: 'conversion',
    align: 'num',
    desc: true,
    hintKey: 'conversionHint',
    value: (a) => a.conversion ?? 0,
  },
  { key: 'games', labelKey: 'games', align: 'num', desc: true, value: (a) => a.games },
];

/* Columnas del ranking de jugadores, con el mismo contrato que COLUMNS:
   clic ordena, primer clic en la direccion util. 'Top 8' va literal: es el
   mismo rotulo en ambos idiomas. */
const PLAYER_COLS = [
  { key: 'name', labelKey: 'tourPlayer', align: 'left', desc: false, value: (p) => p.name },
  { key: 'tournaments', labelKey: 'playersTournaments', align: 'num', desc: true, value: (p) => p.tournaments },
  { key: 'record', labelKey: 'tourRecord', align: 'num', desc: true, value: (p) => p.wins },
  { key: 'top8', label: 'Top 8', align: 'num', desc: true, value: (p) => p.top8 },
  { key: 'firsts', labelKey: 'playersFirsts', align: 'num', desc: true, value: (p) => p.firsts },
  { key: 'winRate', labelKey: 'winRateShort', align: 'num', desc: true, value: (p) => p.winRate ?? -1 },
];

/**
 * Mini-matriz ILUSTRATIVA para el teaser del plan gratis. Nombres publicos del
 * meta y cifras inventadas pero plausibles (los pares casi-espejo suman ~100):
 * los datos reales de la matriz nunca llegan al cliente sin Pro.
 */
const MATRIZ_MUESTRA = [
  { nombre: 'Dragapult', celdas: [null, 0.56, 0.44, 0.61, 0.48] },
  { nombre: 'Gardevoir', celdas: [0.42, null, 0.53, 0.47, 0.58] },
  { nombre: 'Raging Bolt', celdas: [0.54, 0.45, null, 0.39, 0.51] },
  { nombre: 'Gholdengo', celdas: [0.37, 0.51, 0.59, null, 0.44] },
  { nombre: 'Charizard', celdas: [0.5, 0.4, 0.47, 0.54, null] },
];

/**
 * Color de celda: rojo por debajo del 50 %, verde por encima.
 *
 * LAS DOS RAMPAS NO ACABAN EN EL MISMO SITIO, y no es un descuido. El mismo
 * porcentaje de luminosidad en HSL da colores de brillo real muy distinto
 * segun el tono: a L=36 % el verde deja el texto blanco en 3.91:1 -- por
 * debajo del 4.5:1 que pide la AA -- mientras que el rojo, al mismo 36 %, lo
 * deja en 7.91:1. Era el unico fallo de contraste de la aplicacion, y estaba
 * justo en la celda que mas se mira: el emparejamiento que mas se gana.
 *
 * Asi que cada rampa termina donde su color deja de ser legible: el verde a
 * 32 % (blanco a 4.7:1) y el rojo a 36 %, que ahi va sobrado. De regalo se
 * corrige un sesgo de lectura -- el verde se veia mas fuerte que el rojo
 * equivalente, y en una matriz eso hace que lo bueno cante mas que lo malo
 * con el mismo dato detras.
 */
const MAX_LUZ = { verde: 32, rojo: 36 };

function cellStyle(winRate) {
  if (winRate == null) return {};
  const d = Math.max(-0.25, Math.min(0.25, winRate - 0.5)) / 0.25; // -1 … 1
  const verde = d >= 0;
  const hue = verde ? 145 : 5;
  const tope = verde ? MAX_LUZ.verde : MAX_LUZ.rojo;
  return {
    background: `hsl(${hue} 60% ${18 + Math.abs(d) * (tope - 18)}%)`,
    color: Math.abs(d) > 0.55 ? '#fff' : 'var(--text)',
  };
}

/**
 * La escala de un emparejamiento, EN PALABRAS.
 *
 * El color solo no vale: uno de cada doce hombres no distingue el rojo del
 * verde, y ademas nadie ve matices de fondo en un telefono al sol. La palabra
 * es el dato; el color, el refuerzo. Los cortes son los de siempre en este
 * juego -- 45/55 para lo parejo, 40/60 para lo que decide una partida -- y no
 * un degradado continuo, porque "56,3 %" no dice si hay que cambiar de mazo y
 * "Favorable" si.
 */
const ESCALA_MX = [
  { hasta: 0.40, clave: 'mxVeryBad', tono: 'muymal' },
  { hasta: 0.45, clave: 'mxBad', tono: 'mal' },
  { hasta: 0.55, clave: 'mxEven', tono: 'parejo' },
  { hasta: 0.60, clave: 'mxGood', tono: 'bien' },
  { hasta: Infinity, clave: 'mxVeryGood', tono: 'muybien' },
];
const escalaDe = (wr) => ESCALA_MX.find((e) => wr < e.hasta) ?? ESCALA_MX[ESCALA_MX.length - 1];

/**
 * La matriz para el TELEFONO: elige tu mazo y mira contra que pierdes.
 *
 * QUE PROBLEMA RESUELVE. La tabla cruzada mide 581 px y la pantalla 347: casi
 * la mitad de la matriz esta siempre fuera, y comparar una fila con una
 * columna que no se ve a la vez no es "incomodo", es imposible. Encima las
 * cabeceras van en vertical y ocupan 92 px de alto para caber. Comprimir la
 * tabla de escritorio no era la solucion; la pregunta que se le hace a una
 * matriz en el movil es otra y mas concreta: "llevo ESTE mazo, ¿contra que me
 * va mal?". Eso es una lista, no una tabla.
 *
 * LA BARRA NACE EN EL 50 %, no en el cero. Un 45 % en una barra normal se
 * pinta casi medio llena y se lee como "vas mas o menos"; naciendo en el
 * centro, ese mismo 45 % es un trozo que crece HACIA LA IZQUIERDA y se lee
 * como lo que es: vas por debajo. El eje del medio es la unica referencia que
 * importa en un win rate.
 *
 * Las dos formas -- tabla y lista -- se pintan siempre y es el CSS quien
 * decide cual se ve, igual que la barra de orden de los arquetipos: asi el
 * estado es uno solo y no hay dos ramas que se desincronicen.
 */
function MatrizMovil({ matrix, mio, onMio, orden, onOrden, t, lang }) {
  const filas = matrix?.matrix ?? [];
  /* El mazo elegido se valida contra los datos de AHORA: al cambiar de formato
     o de ventana, el arquetipo que estaba elegido puede no estar en la tabla
     nueva, y sin esto la lista se quedaba en blanco sin decir por que.

     Por defecto, el primero que NO sea `other`. El cajon de sastre encabeza la
     tabla porque suma mucha presencia, pero nadie "lleva Other": abrir la
     pestaña con el elegido era pedirle al visitante que descartara la
     respuesta antes de leerla. Como RIVAL si se queda en la lista -- cruzarse
     con lo que nadie sabe nombrar es parte de un torneo. */
  const yo = filas.find((m) => m.id === mio)
    ?? filas.find((m) => m.id !== 'other')
    ?? filas[0];
  if (!yo) return null;

  const rivales = yo.vs
    .filter((c) => !c.mirror)
    .map((c) => ({ ...c, name: filas.find((m) => m.id === c.id)?.name ?? c.id }))
    .sort((a, b) => {
      /* Lo que no tiene muestra se va SIEMPRE al final, ordene como ordene:
         no es ni un buen emparejamiento ni uno malo, es una pregunta sin
         responder, y colarlo entre los datos buenos lo disfraza de dato. */
      if ((a.winRate == null) !== (b.winRate == null)) return a.winRate == null ? 1 : -1;
      if (a.winRate == null) return b.games - a.games;
      return orden === 'peor' ? a.winRate - b.winRate : b.winRate - a.winRate;
    });

  return (
    <div className="mxlista">
      <div className="mxlista__mandos">
        <Dropdown
          label={t('mxPickDeck')}
          value={yo.id}
          onChange={(v) => onMio(v ?? yo.id)}
          options={filas.map((m) => ({ key: m.id, label: m.name }))}
        />
        {/* Un solo boton que alterna, no dos: son las dos caras de la misma
            decision y `aria-pressed` ya cuenta en cual esta. */}
        <button
          type="button"
          className="meta__chip is-active mxlista__orden"
          aria-pressed={orden === 'peor'}
          onClick={() => onOrden(orden === 'peor' ? 'mejor' : 'peor')}
        >
          {orden === 'peor' ? t('mxWorstFirst') : t('mxBestFirst')}
        </button>
      </div>

      <ol className="mxlista__filas">
        {rivales.map((r) => {
          const sin = r.winRate == null;
          const esc = sin ? null : escalaDe(r.winRate);
          /* Cuanto se separa del centro, de 0 a 1, topado a ±25 puntos: es la
             misma referencia que usa el color de la tabla grande, para que las
             dos formas cuenten lo mismo con la misma intensidad. */
          const desvio = sin ? 0 : Math.max(-1, Math.min(1, (r.winRate - 0.5) / 0.25));
          const aria = sin
            ? t('mxRowAriaNoData', { a: yo.name, b: r.name, n: r.games })
            : t('mxRowAria', {
                a: yo.name, b: r.name,
                p: Math.round(r.winRate * 100), n: r.games, v: t(esc.clave),
              });
          return (
            <li
              key={r.id}
              className={`mxlista__fila${sin ? ' is-sinmuestra' : ''}`}
              /* La fila entera se anuncia de una vez: leer "Charizard", "44 %",
                 "128", "desfavorable" en cuatro paradas obliga a recomponer la
                 frase de memoria. */
              aria-label={aria}
            >
              <span className="mxlista__rival" aria-hidden="true">
                {matrix.nucleos?.[r.id]?.length > 0 && (
                  <span className="mxlista__minis">
                    {matrix.nucleos[r.id].slice(0, 2).map((c) => (
                      <Mini key={c.cardId} carta={{ ...c, count: 1 }} />
                    ))}
                  </span>
                )}
                {r.name}
              </span>
              <span className="mxlista__medida" aria-hidden="true">
                <span className="mxlista__barra">
                  <span className="mxlista__eje" />
                  {!sin && (
                    /* Se estira con `scaleX` sobre un ancho fijo, no cambiando
                       el ancho: asi el cambio de mazo lo anima la tarjeta
                       grafica sin recalcular la maquetacion de diez filas. */
                    <span
                      className={`mxlista__trozo is-${esc.tono} ${desvio < 0 ? 'is-izq' : 'is-der'}`}
                      style={{ transform: `scaleX(${Math.abs(desvio)})` }}
                    />
                  )}
                </span>
              </span>
              <span className="mxlista__cifra" aria-hidden="true">
                {sin ? '—' : pct(r.winRate, 0)}
              </span>
              <span className={`mxlista__etiq${sin ? '' : ` is-${esc.tono}`}`} aria-hidden="true">
                {sin ? t('mxNoData') : t(esc.clave)}
                <span className="mxlista__muestra">
                  {' · '}
                  {new Intl.NumberFormat(lang === 'es' ? 'es-CL' : 'en-US').format(r.games)}{' '}
                  {t('games')}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * Detalle de un arquetipo: que cartas lleva y en cuantas listas aparece.
 *
 * Las barras entran escalonadas con un retardo proporcional al indice, pero el
 * escalonado se corta a los 12 primeros: mas alla el ultimo tardaria casi un
 * segundo en aparecer y se leeria como lentitud, no como elegancia.
 */
/** Mejores y peores emparejamientos, para no tener que ir a buscar la matriz. */
function MatchupHighlights({ data, t }) {
  if (!data || (!data.best?.length && !data.worst?.length)) return null;

  const Row = ({ m }) => (
    <li>
      {/* Las caras del rival, el patron de toda lista de mazos. El hueco es
          fijo (dos minis) para que los nombres alineen entre filas. */}
      <span className="meta__muMinis" aria-hidden="true">
        {(m.nucleo ?? []).slice(0, 2).map((c) => (
          <Mini key={c.cardId} carta={{ ...c, count: 1 }} />
        ))}
      </span>
      <span className="meta__muName">{m.name}</span>
      <span className="meta__muWr" style={cellStyle(m.winRate)}>{pct(m.winRate, 0)}</span>
      <span className="muted meta__muGames">{m.games}</span>
    </li>
  );

  return (
    <div className="meta__mu">
      {data.expectedWinRate != null && (
        <p className="meta__expected">
          {t('expectedVsField')}{' '}
          <strong style={cellStyle(data.expectedWinRate)}>{pct(data.expectedWinRate)}</strong>
          <span className="muted"> · {t('fieldCovered')} {pct(data.fieldCovered, 0)}</span>
        </p>
      )}
      {/*
        Dos tarjetas con tinte propio, no dos listas desnudas: es LA pregunta
        que uno trae al abrir un arquetipo ("¿contra que va bien? ¿que le
        cuesta?") y estaba en el mismo gris que todo lo demas. Los rotulos
        hablan de RENDIMIENTO (favorable / cuesta arriba), no de calidad:
        "mejores y peores" sonaba a juicio sobre los otros mazos.
      */}
      <div className="meta__mucols">
        <div className="meta__mucol is-favor">
          <h5>
            <span className="meta__mudot" aria-hidden="true" />
            {t('bestMatchups')}
          </h5>
          <ul>{data.best.map((m) => <Row key={m.id} m={m} />)}</ul>
        </div>
        <div className="meta__mucol is-contra">
          <h5>
            <span className="meta__mudot" aria-hidden="true" />
            {t('worstMatchups')}
          </h5>
          <ul>{data.worst.map((m) => <Row key={m.id} m={m} />)}</ul>
        </div>
      </div>
    </div>
  );
}

function ArchetypeCards({ cards, matchups, name, t, onOpenCard }) {
  if (!cards) return <Cargando texto={t('loading')} className="meta__expandLoading" />;

  return (
    <>
      <MatchupHighlights data={matchups} t={t} />
      {/* h2 y no h4: en esta pagina el unico titulo por encima es el h1, asi
          que un h4 se saltaba dos niveles (axe: `heading-order`). El tamaño lo
          decide la clase, no la etiqueta. */}
      <h2 className="meta__expandTitle">
        {t('coreCards')} — {name}
        <span className="muted"> · {cards.lists} {t('lists')}</span>
      </h2>
      <ul className="meta__cardlist">
        {cards.cards.slice(0, 30).map((c, i) => (
          <li key={c.name} style={{ '--delay': `${Math.min(i, 12) * 22}ms` }}>
            {/* Boton y no <li> pinchable: aqui se abre la ficha de la carta,
                asi que tiene que ser alcanzable con el teclado igual que el
                resto de accesos a la ficha. */}
            <button
              type="button"
              className="meta__cardbtn"
              disabled={!c.cardId}
              onClick={(e) => {
                e.stopPropagation();     // no plegar el arquetipo
                if (c.cardId) {
                  onOpenCard(c.cardId, cards.cards.slice(0, 30).map((x) => x.cardId).filter(Boolean));
                }
              }}
            >
              {cardArt(c) && (
                <span
                  className="rowart"
                  style={{ '--art': `url(${cardArt(c)})` }}
                  aria-hidden="true"
                />
              )}
              <span className="meta__bar" style={{ '--w': `${c.inclusion * 100}%` }} />
              <span className="meta__cardname">{c.name}</span>
              <span className="meta__cardnum muted">
                {pct(c.inclusion, 0)} · {c.avgCopies.toFixed(1)}×
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * Tendencia del arquetipo: su cuota semana a semana, como linea sobria (el
 * mismo lenguaje que la tendencia de precio de la ficha). Responde "¿sube o
 * baja?" sin pedir que nadie compare tablas de memoria. Con menos de dos
 * semanas con muestra decente no se pinta nada: una linea de un punto es un
 * adorno.
 */
function TendenciaArquetipo({ trend, t, lang }) {
  const pts = (trend?.weeks ?? []).filter((w) => w.players >= 20);
  if (pts.length < 2) return null;
  const vals = pts.map((p) => p.share);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const W = 220;
  const H = 40;
  const PAD = 4;
  const x = (i) => PAD + (i * (W - 2 * PAD)) / (pts.length - 1);
  const y = (v) => (max === min ? H / 2 : H - PAD - ((v - min) * (H - 2 * PAD)) / (max - min));
  const linea = vals.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const fecha = (d) =>
    new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString(
      lang === 'es' ? 'es-CL' : 'en-US',
      { day: 'numeric', month: 'short' }
    );
  return (
    <div className="metatrend">
      <p className="metatrend__titulo muted">
        {t('metaTrend')} · {t('metaTrendWeeks', { n: pts.length })}
      </p>
      <svg viewBox={`0 0 ${W} ${H}`} className="metatrend__svg" role="img" aria-label={t('metaTrend')}>
        <polyline
          points={linea}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="1.8"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <circle cx={x(vals.length - 1)} cy={y(vals[vals.length - 1])} r="2.6" fill="var(--accent)" />
      </svg>
      <div className="metatrend__pie muted">
        <span>{fecha(pts[0].from)} · {(vals[0] * 100).toFixed(1)} %</span>
        <span>{fecha(pts[pts.length - 1].from)} · {(vals[vals.length - 1] * 100).toFixed(1)} %</span>
      </div>
    </div>
  );
}

export default function MetaView({ onOpenCard, arquetipoInicial = null }) {
  const { lang, t } = useLang();
  /* Solo para elegir el rotulo del gate: al ANONIMO se le ofrece la prueba
     ("3 dias gratis"), que es su camino real; a quien ya tiene cuenta -- y
     quiza gasto su trial -- prometerselo seria mentirle. */
  const { user } = useAuth();
  /*
   * LOS FILTROS DE ESTA VISTA VIVEN EN LA URL (estado-url.js).
   *
   * Cambiar de pestaña REMONTA la vista (App le pone una `key` nueva), asi que
   * ir a mirar una carta y volver al meta borraba el periodo, el formato, la
   * subpestaña y la busqueda: se volvia a "arquetipos, 7 dias, Standard" como
   * si no hubieras estado. Es el mismo agujero que se tapo en el catalogo de
   * mazos, y aqui trae ademas lo que el meta pedia a gritos: un enlace que
   * lleva a lo que estas mirando -- "el meta de 90 dias, ordenado, con este
   * arquetipo buscado" -- en vez de a la portada.
   *
   * Las listas blancas son las mismas opciones que ofrecen los desplegables:
   * un `dias=999` escrito a mano vuelve al valor de siempre en vez de pedirle
   * al servidor un periodo que no existe.
   */
  const [days, setDays] = useEstadoUrl('dias', 7, { valores: [7, 30, 90, 0] });
  /**
   * Formato. Standard por defecto porque es lo que juega casi todo el mundo, y
   * porque mezclar formatos no es un detalle: Expanded juega con otro pool, así
   * que sus arquetipos y sus emparejamientos no comparan con los de Standard.
   */
  const [format, setFormat] = useEstadoUrl('formato', 'STANDARD');
  const [formats, setFormats] = useState([]);
  // Orden como {columna, direccion}: la cabecera necesita saber las dos cosas
  // para pintar la flecha y para decidir que hace el proximo clic.
  // Por VICTORIAS y no por presencia: la pregunta con la que se abre el meta
  // es "que esta ganando", no "que juega mas gente". La muestra minima de
  // abajo es lo que evita que los primeros puestos sean mazos de 4 partidas.
  const [sort, setSort] = useState({ key: 'winRate', dir: 'desc' });
  // Antes era un `games >= 20` escrito a fuego dentro del filtrado: invisible y
  // sin forma de aflojarlo. Es justo la palanca que hace util ordenar por
  // victorias, porque sin muestra minima los primeros puestos se los llevan
  // mazos con cuatro partidas.
  const [minGames, setMinGames] = useEstadoUrl('muestra', 20, { valores: [0, 20, 50, 100, 250] });
  /**
   * Tamaño mínimo del TORNEO (no de la muestra del arquetipo).
   *
   * Existía en la API desde el principio y nadie lo usaba. Es lo que hace
   * comparable la conversión a top 8: en un torneo de 35 el top 8 es el 23 % del
   * campo y en uno de 416 es el 2 %, así que sin acotar el tamaño se está
   * sumando peras con manzanas.
   */
  const [minPlayers, setMinPlayers] = useEstadoUrl('tamano', 0, { valores: [0, 64, 128, 256] });
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState(null);
  /**
   * Que se ha caido, por pestaña.
   *
   * QUE PROBLEMA RESUELVE. Las peticiones de esta vista acababan todas en un
   * `.catch(() => {})` o, peor, en un `setData({ archetypes: [] })`: si la API
   * no contestaba, la pantalla no decia "no se pudo cargar" sino "no hay
   * suficientes partidas en este periodo". Es la peor mentira que puede contar
   * una herramienta de datos -- la unica lectura posible es que el sitio esta
   * vacio -- y ademas dejaba sin salida: nada que reintentar, porque segun la
   * pantalla no habia pasado nada.
   *
   * Una bandera por pestaña y no una global: que se caiga la matriz no debe
   * tachar la tabla de arquetipos, que puede estar perfectamente servida desde
   * la cache. Se limpia en cada respuesta buena, asi que un reintento con
   * exito borra el aviso sin tener que acordarse de nada.
   */
  const [fallos, setFallos] = useState({});
  const marcarFallo = (zona, hay) =>
    setFallos((f) => (!!f[zona] === hay ? f : { ...f, [zona]: hay }));
  /** Hay una recarga en curso con datos viejos aun en pantalla. */
  const [refrescando, setRefrescando] = useState(false);
  const [summary, setSummary] = useState(null);
  /** Edad de la ultima ingesta del meta, para el "actualizado hace X". */
  const [metaEdadMs, setMetaEdadMs] = useState(null);
  const [openArch, setOpenArch] = useState(null);
  // Se mantiene montada la fila que se cierra durante la animacion de salida;
  // desmontarla de golpe hace que el detalle desaparezca de un tiron.
  const [closingArch, setClosingArch] = useState(null);
  const [cards, setCards] = useState(null);
  const [archMatchups, setArchMatchups] = useState(null);
  const [archTrend, setArchTrend] = useState(null);
  // La matriz completa vive en su propia pestaña: con 45 arquetipos en la tabla
  // siempre quedaria enterrada al final del scroll.
  const [pane, setPane] = useEstadoUrl('pane', 'archetypes', {
    valores: ['archetypes', 'tournaments', 'players', 'matchups'],
  });
  /*
   * PROXIMOS TORNEOS. No salen de la base: los lee el servidor de las dos
   * paginas publicas de Limitless (ver proximos.js), porque su API solo sirve
   * torneos ya terminados.
   *
   * `region` arranca en LATAM y no en Chile aunque este sitio sea chileno, y
   * es una decision con datos detras: en el calendario oficial hay UN torneo
   * chileno (Regional Santiago, feb-2027) de 36, y los de comunidad no traen
   * pais. Abriendo en "Chile" la pantalla estrenaria con una sola fila y
   * pareceria rota. Con Latinoamerica son diez, Chile queda a un toque y el
   * Regional sale destacado igual.
   */
  const [proximos, setProximos] = useState(null);
  const [proxFallo, setProxFallo] = useState(false);
  /*
   * Dentro de Torneos, que se mira: lo que viene o lo que ya paso. Arranca en
   * PROXIMOS porque es lo unico de esta vista a lo que se puede reaccionar --
   * inscribirse -- y lo pasado sigue a un toque; ademas el resto del meta
   * entero ya cuenta lo que ya se jugo.
   */
  const [vistaTorneos, setVistaTorneos] = useEstadoUrl('torneos', 'proximos', {
    valores: ['proximos', 'pasados'],
  });
  const [region, setRegion] = useEstadoUrl('region', 'latam', {
    valores: ['latam', 'cl', 'online', 'todos'],
  });
  const [soloAbiertos, setSoloAbiertos] = useEstadoUrl('abiertos', false);
  /*
   * TORNEOS: los datos crudos que alimentan todo el metajuego, enseñados por
   * fin como lo que son. La lista se pide solo al entrar en la pestaña (es la
   * unica consulta de esta vista que no hace falta para nada mas), y el
   * detalle de un torneo al desplegarlo.
   */
  const [tourneys, setTourneys] = useState(null);
  const [openTour, setOpenTour] = useState(null);
  const [tourDetail, setTourDetail] = useState(null);
  /* Paginacion de torneos: 10 por pagina, como el ranking. Cambiar filtros
     vuelve a la primera (misma razon: la pagina 4 de otra lista no es nada). */
  const [tourPagina, setTourPagina] = useState(1);
  useEffect(() => {
    setTourPagina(1);
    setPlayersPagina(1);
  }, [days, format, minPlayers, pane]);
  /** Jugador cuya lista se esta pidiendo, para el estado del boton pulsado. */
  const [listaBusy, setListaBusy] = useState(null);
  /** Perfil de jugador abierto desde una clasificacion (id de jugador). */
  const [perfil, setPerfil] = useState(null);
  /** Ranking global de jugadores (pestaña Jugadores) y su pagina. */
  const [playersRank, setPlayersRank] = useState(null);
  const [playersPagina, setPlayersPagina] = useState(1);
  /*
   * Orden del ranking de jugadores. `null` = el del servidor (top 8, luego
   * titulos, luego victorias), que es un empate compuesto que ninguna columna
   * sola reproduce; en cuanto se pincha una cabecera manda esa columna.
   */
  const [playersSort, setPlayersSort] = useState(null);
  const togglePlayersSort = (col) =>
    setPlayersSort((s) =>
      s?.key === col.key
        ? { key: col.key, dir: s.dir === 'desc' ? 'asc' : 'desc' }
        : { key: col.key, dir: col.desc ? 'desc' : 'asc' }
    );
  /* Buscador por nombre, insensible a acentos y mayusculas, como el de
     arquetipos. Busca sobre lo cargado (el top del periodo): encontrar a
     cualquier jugador del mundo es trabajo del perfil, no de este ranking. */
  const [busquedaJugador, setBusquedaJugador] = useEstadoUrl('jugador', '');
  useEffect(() => {
    setPlayersPagina(1);
  }, [playersSort, busquedaJugador]);
  const playersOrdenados = useMemo(() => {
    const plano = (s) =>
      String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    let lista = playersRank?.players ?? [];
    if (busquedaJugador.trim()) {
      const q = plano(busquedaJugador.trim());
      lista = lista.filter((p) => plano(p.name).includes(q) || plano(p.player).includes(q));
    }
    if (!playersSort) return lista;
    const col = PLAYER_COLS.find((c) => c.key === playersSort.key);
    if (!col) return lista;
    const dir = playersSort.dir === 'asc' ? 1 : -1;
    return [...lista].sort((a, b) => {
      const va = col.value(a);
      const vb = col.value(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return cmp * dir;
    });
  }, [playersRank, playersSort, busquedaJugador]);
  /** Detalle de torneo: clasificacion o rondas, y las rondas cargadas. */
  const [vistaTour, setVistaTour] = useState('standings');
  const [rondasTour, setRondasTour] = useState(null);
  const [rondaSel, setRondaSel] = useState(null);
  const [types, setTypes] = useState([]);
  /* El tipo NO lleva lista blanca: sus claves las manda el servidor
     (`/api/meta/types`) y aqui aun no han llegado cuando se lee la URL. */
  const [typeFilter, setTypeFilter] = useEstadoUrl('tipo', null);
  const [sheet, setSheet] = useState(null);      // {deck, name} de la hoja abierta
  const [sheetBusy, setSheetBusy] = useState(false);
  // Recorte por plan y candado. El candado se abre al PULSAR, nunca solo.
  const [truncated, setTruncated] = useState(null);
  const [matrixLocked, setMatrixLocked] = useState(false);
  /**
   * El mazo elegido en la matriz del telefono, y el orden de sus rivales.
   *
   * `null` significa "el primero de la tabla", que es el mas jugado: quien
   * abre la pestaña sin elegir nada ve el emparejamiento que mas le importa a
   * mas gente. No se guarda entre visitas a proposito -- el mazo que preparas
   * cambia de semana en semana y una preferencia invisible acabaria enseñando
   * el mazo del mes pasado.
   */
  const [mxMio, setMxMio] = useState(null);
  /* Peores primero: se mira una matriz para saber contra que se pierde. */
  const [mxOrden, setMxOrden] = useState('peor');

  /**
   * La banda de pestañas y filtros se queda PEGADA, y al pegarse cuenta que
   * estas mirando.
   *
   * QUE PROBLEMA RESUELVE. Formato, periodo y fecha de actualizacion viven en
   * la cabecera, y la cabecera se va con el scroll: tres pantallas de tabla
   * mas abajo -- o en cualquiera de las otras tres pestañas -- ya no habia
   * forma de saber si ese 56 % es de la ultima semana o de noventa dias, ni de
   * que formato. Un porcentaje sin periodo no es un dato, es una anecdota.
   *
   * El contexto NO se pinta en reposo: ahi la cabecera ya lo dice entera y
   * repetirlo seria ruido. Aparece solo cuando la banda se despega, que es
   * justo cuando dejo de verse. Se decide MIDIENDO si esta pegada -- no con un
   * umbral de scrollY a ojo --, y el anclaje se lee del propio CSS, donde vive
   * el numero: es el mismo mecanismo que `is-elevada` en Nav.jsx y que el
   * "volver a mis mazos", por los mismos motivos que se explican alli.
   */
  const bandaRef = useRef(null);
  const [bandaPegada, setBandaPegada] = useState(false);
  useEffect(() => {
    const mirar = () => {
      const el = bandaRef.current;
      if (!el) return;
      const ancla = parseFloat(getComputedStyle(el).top) || 0;
      setBandaPegada(el.getBoundingClientRect().top <= ancla + 0.5);
    };
    mirar();
    window.addEventListener('scroll', mirar, { passive: true });
    window.addEventListener('resize', mirar);
    return () => {
      window.removeEventListener('scroll', mirar);
      window.removeEventListener('resize', mirar);
    };
  }, []);

  /**
   * Publica el alto de la banda como `--meta-banda-alto`, hermano de
   * `--nav-alto` y por el mismo motivo exacto.
   *
   * LO QUE ROMPIA SI NO. La matriz tiene su propia cabecera pegajosa -- los
   * nombres de arquetipo, que son lo unico que hace legible una tabla cruzada
   * -- anclada al borde de la ventana. Al volverse pegajosa la banda, esa
   * cabecera se quedaba DEBAJO de ella: se pegaba, si, pero tapada, que es
   * igual que no pegarse. Ahora se ancla bajo las dos franjas sumadas.
   *
   * Se mide en vez de clavarse a mano porque el alto cambia solo: crece 26 px
   * cuando la banda se despega y saca su linea de contexto, y cambia tambien
   * con el idioma y con el ancho de la ventana.
   */
  useEffect(() => {
    const el = bandaRef.current;
    if (!el) return undefined;
    const medir = () => {
      const alto = Math.round(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--meta-banda-alto', `${alto}px`);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => {
      ro.disconnect();
      /* Al salir del meta la variable dejaria de tener sentido y cualquier
         pegajoso que la use se anclaria contra un alto fantasma. */
      document.documentElement.style.removeProperty('--meta-banda-alto');
    };
  }, []);

  /*
   * LAS CIFRAS DEL PULSO LLEVAN A SU SITIO: "418 torneos" abre los torneos ya
   * jugados (los proximos no son los que se cuentan ahi), "jugadores" el
   * ranking y "partidas" la matriz, que es donde las partidas se leen como
   * cruces. Un numero que se puede pulsar contesta "¿cuales?" sin buscar la
   * pestaña.
   *
   * Solo se desplaza si el contenido de la pestaña queda fuera de la vista
   * (en el telefono la cabecera ocupa media pantalla); en escritorio ya se ve
   * y mover la pagina sin necesidad desorientaria.
   */
  const irA = (destino) => {
    if (destino === 'tournaments') setVistaTorneos('pasados');
    setPane(destino);
    requestAnimationFrame(() => {
      const el = bandaRef.current;
      if (!el) return;
      const caja = el.getBoundingClientRect();
      if (caja.bottom < window.innerHeight * 0.55) return;
      const ancla = parseFloat(getComputedStyle(el).top) || 0;
      const suave = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: window.scrollY + caja.top - ancla, behavior: suave ? 'smooth' : 'auto' });
    });
  };
  /*
   * Celda de la matriz bajo el raton, para RESALTAR EN CRUZ su fila y su
   * columna. Con 10x10 porcentajes iguales de color, seguir con el ojo que
   * celda cruza que arquetipos es lo que hacia la matriz "poco entendible":
   * la cruz contesta "quien contra quien" sin contar filas con el dedo.
   */
  const [cruz, setCruz] = useState(null); // { fila, col } o null
  const [gate, setGate] = useState(null);

  /*
   * Cuando el panel de "Datos" del admin termina una ingesta, la base acaba de
   * cambiar y esta vista tiene la tabla vieja en pantalla. El aviso
   * (ptcg:datos-frescos) suma aqui y el nonce reengancha los efectos de carga:
   * se vuelve a pedir todo SIN remontar la vista, que es lo que conserva
   * filtros, orden y scroll. Antes habia que recargar la pagina a mano.
   */
  const [nonceDatos, setNonceDatos] = useState(0);
  useEffect(() => {
    const alFresco = () => setNonceDatos((n) => n + 1);
    window.addEventListener('ptcg:datos-frescos', alFresco);
    return () => window.removeEventListener('ptcg:datos-frescos', alFresco);
  }, []);

  /*
   * Si la fila de chips de orden se puede seguir desplazando a la derecha.
   * Mismo patron medido que las pastillas de mazos en Partidas: el desvanecido
   * solo se enciende cuando de verdad queda algo por ver.
   */
  const chipsRef = useRef(null);
  const [chipsHayMas, setChipsHayMas] = useState(false);
  useEffect(() => {
    const el = chipsRef.current;
    if (!el) return undefined;
    const medir = () => setChipsHayMas(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    medir();
    el.addEventListener('scroll', medir, { passive: true });
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', medir);
      ro.disconnect();
    };
  });

  /*
   * TODO LO DE ESTA VISTA ARRANCA EN CALIENTE (ver cache.js): el meta se
   * ingiere una vez al día y el catálogo de tipos cambia con las cartas, así
   * que lo cacheado de esta sesión se pinta al instante y la petición de
   * detrás solo corrige si algo cambió. La pokébola queda para la primera
   * visita de verdad. Las claves llevan al usuario dentro porque el PLAN
   * recorta lo que el servidor devuelve (ventana, arquetipos, matriz).
   */
  useEffect(() => {
    const clave = claveCache(`/api/types?lang=${lang}`, 0);
    const enCache = leerCache(clave);
    if (enCache) setTypes(enCache);
    api(`/api/types?lang=${lang}`)
      .then((r) => r.json())
      .then((d) => {
        guardarCache(clave, d.types ?? []);
        setTypes(d.types ?? []);
      })
      .catch(() => {});
  }, [lang, nonceDatos]);

  /**
   * Abre la hoja del mazo con el AGREGADO del arquetipo al lado.
   *
   * La hoja es una lista real y concreta, y el agregado es lo que juegan las
   * ~120 listas del arquetipo. Verlas por separado hacia que la hoja pareciera
   * contradecir al detalle: aparecen cartas que casi nadie juega (elecciones del
   * jugador) y faltan otras que juega casi todo el mundo. Cruzandolas, esa
   * diferencia deja de ser ruido y pasa a ser lo interesante de la pantalla.
   *
   * El agregado se reaprovecha si ya se cargo al desplegar la fila, que es el
   * camino habitual para llegar aqui.
   */
  const openSheet = async (id, name) => {
    setSheetBusy(true);
    try {
      const [deckRes, inclusion] = await Promise.all([
        api(`/api/meta/archetypes/${encodeURIComponent(id)}/decklist?days=${days}&format=${format}&lang=${lang}`),
        cards?.deckId === id
          ? Promise.resolve(cards)
          : api(`/api/meta/archetypes/${encodeURIComponent(id)}/cards?days=${days}&format=${format}&lang=${lang}`)
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null),
      ]);
      if (!deckRes.ok) return;
      setSheet({ deck: await deckRes.json(), name, inclusion });
    } finally {
      setSheetBusy(false);
    }
  };

  /*
   * NOTA: aqui vivio el salto desde Partidas ("ver el mazo del rival" abria
   * esta vista y desplazaba hasta la fila). En movil el aterrizaje nunca fue
   * fiable, asi que Partidas abre ahora el DeckSheet directamente y el salto
   * entre vistas desaparecio.
   */

  const toggle = (id) => {
    if (openArch === id) {
      setClosingArch(id);
      setOpenArch(null);
      setTimeout(() => setClosingArch(null), 200);
    } else {
      setClosingArch(null);
      setOpenArch(id);
    }
  };

  useEffect(() => {
    /*
     * El resumen arranca con el último valor conocido -- las cifras cuentan
     * desde él hacia el fresco en vez de desde cero -- y el sondeo de cada
     * minuto sigue igual: la caché quita la espera inicial, no el "en vivo".
     */
    const claveResumen = claveCache('/api/meta/summary', 0);
    const resumenCache = leerCache(claveResumen);
    if (resumenCache) setSummary(resumenCache);
    const pedirResumen = () =>
      api('/api/meta/summary')
        .then((r) => r.json())
        .then((d) => {
          guardarCache(claveResumen, d);
          setSummary(d);
        })
        .catch(() => {});
    pedirResumen();
    /*
     * El resumen se SONDEA mientras la vista esta abierta. Las cifras cambian
     * cuando el planificador ingiere torneos nuevos, y verlas moverse solas es
     * lo que hace creible el "en vivo" del rotulo. Son dos COUNT baratos y solo
     * mientras la pestaña del meta esta montada: al salir, el intervalo muere.
     */
    const cada = setInterval(pedirResumen, 60 * 1000);
    // Los formatos salen de lo que hay ingestado: ofrecer los que no tenemos
    // sería enseñar filtros que devuelven una tabla vacía.
    const claveFmt = claveCache('/api/meta/formats', 0);
    const fmtCache = leerCache(claveFmt);
    if (fmtCache) setFormats(fmtCache);
    api('/api/meta/formats')
      .then((r) => r.json())
      .then((d) => {
        guardarCache(claveFmt, d.formats ?? []);
        setFormats(d.formats ?? []);
      })
      .catch(() => {});
    // De cuando es la ultima ingesta del meta: es el dato que responde "¿como
    // de en vivo?" con un numero en vez de con una promesa.
    api('/api/refresh/status')
      .then((r) => r.json())
      .then((d) => {
        const meta = (d.jobs ?? []).find((j) => j.name === 'meta');
        if (meta?.ageMs != null) setMetaEdadMs(meta.ageMs);
      })
      .catch(() => {});
    return () => clearInterval(cada);
  }, [nonceDatos]);

  useEffect(() => {
    let alive = true;
    /*
     * La tabla anterior SE QUEDA mientras llega la nueva, atenuada.
     *
     * Antes esto hacia `setData(null)`: cada cambio de filtro desmontaba la
     * tabla entera, aparecia el "cargando" y todo lo de debajo saltaba de
     * sitio dos veces. Conservando el dato viejo la pagina no cambia de
     * dimensiones; el velo (is-refrescando) dice "esto se esta actualizando"
     * sin mover un pixel. El unico "cargando" de verdad es el primero, cuando
     * aun no hay nada que enseñar.
     */
    /*
     * Con caché, la tabla de la última visita se pinta YA (velo de refresco
     * encima) y la respuesta fresca la sustituye. El velo es el mismo de
     * cambiar filtros: dice "actualizándose" sin mover un píxel.
     */
    const u = user?.id ?? 0;
    const claveArq = claveCache(
      `/api/meta/archetypes?days=${days}&format=${format}&minPlayers=${minPlayers}`, u);
    const arqCache = leerCache(claveArq);
    if (arqCache) {
      setData(arqCache);
      setTruncated(arqCache.truncated ?? null);
    }
    setRefrescando(true);
    api(`/api/meta/archetypes?days=${days}&format=${format}&minPlayers=${minPlayers}`)
      /* Un 500 que devuelve JSON valido pasaba por bueno y se pintaba como una
         tabla sin arquetipos. El estado manda antes que el cuerpo. */
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (!alive) return;
        guardarCache(claveArq, d);
        setData(d);
        setTruncated(d.truncated ?? null);
        marcarFallo('archetypes', false);
      })
      .catch(() => alive && marcarFallo('archetypes', true))
      .finally(() => alive && setRefrescando(false));
    const claveMx = claveCache(`/api/meta/matchups?days=${days}&format=${format}&top=10`, u);
    const mxCache = leerCache(claveMx);
    if (mxCache) {
      setMatrixLocked(false);
      setMatrix(mxCache);
    }
    api(`/api/meta/matchups?days=${days}&format=${format}&top=10`)
      .then(async (r) => {
        if (!alive) return;
        // 403 aqui no es un error que ocultar: es que el plan no la incluye, y
        // la pestaña tiene que decirlo en vez de quedarse en blanco. El 403 no
        // se cachea: la clave por usuario ya evita heredar la matriz de otro.
        setMatrixLocked(r.status === 403);
        if (r.ok) {
          const j = await r.json();
          guardarCache(claveMx, j);
          setMatrix(j);
          marcarFallo('matrix', false);
        } else {
          setMatrix(null);
          /* El 403 no es un fallo: es el candado del plan, y tiene su propio
             cartel. Cualquier otro codigo si lo es. */
          marcarFallo('matrix', r.status !== 403);
        }
      })
      .catch(() => alive && marcarFallo('matrix', true));
    return () => {
      alive = false;
    };
    // `user?.id` fuera de las dependencias a propósito: entrar o salir de la
    // cuenta ya remonta la vista entera (la key de App.jsx lleva id y plan).
  }, [days, format, minPlayers, nonceDatos]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!openArch) return;
    let alive = true;
    const u = user?.id ?? 0;
    const id = encodeURIComponent(openArch);
    /* Reabrir un arquetipo ya visitado pinta su detalle al instante; el
       loader del desplegable queda para la primera vez. */
    const claveCartas = claveCache(
      `/api/meta/archetypes/${id}/cards?days=${days}&format=${format}&lang=${lang}`, u);
    const claveMus = claveCache(
      `/api/meta/archetypes/${id}/matchups?days=${days}&format=${format}`, u);
    setCards(leerCache(claveCartas) ?? null);
    setArchMatchups(leerCache(claveMus) ?? null);
    api(`/api/meta/archetypes/${id}/cards?days=${days}&format=${format}&lang=${lang}`)
      .then((r) => r.json())
      .then((d) => {
        guardarCache(claveCartas, d);
        if (alive) setCards(d);
      })
      .catch(() => {});
    api(`/api/meta/archetypes/${id}/matchups?days=${days}&format=${format}`)
      .then((r) => r.json())
      .then((d) => {
        guardarCache(claveMus, d);
        if (alive) setArchMatchups(d);
      })
      .catch(() => {});
    /* La tendencia semanal no depende de la ventana elegida (siempre son las
       ultimas semanas): solo del formato. */
    const claveTrend = claveCache(`/api/meta/archetypes/${id}/trend?format=${format}`, u);
    setArchTrend(leerCache(claveTrend) ?? null);
    api(`/api/meta/archetypes/${id}/trend?format=${format}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        guardarCache(claveTrend, d);
        if (alive) setArchTrend(d);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // `user?.id` fuera por lo mismo que arriba: el login remonta la vista.
  }, [openArch, days, format, lang, nonceDatos]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* La lista de torneos, solo con la pestaña abierta. Misma cache de sesion
     que el resto: reentrar pinta al instante y la respuesta fresca corrige. */
  useEffect(() => {
    if (pane !== 'tournaments') return undefined;
    let alive = true;
    const u = user?.id ?? 0;
    const ruta = `/api/meta/tournaments?days=${days}&format=${format}&minPlayers=${minPlayers}`;
    const clave = claveCache(ruta, u);
    const enCache = leerCache(clave);
    if (enCache) setTourneys(enCache);
    api(ruta)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (!alive) return;
        guardarCache(clave, d);
        setTourneys(d);
        marcarFallo('tournaments', false);
      })
      .catch(() => alive && marcarFallo('tournaments', true));
    return () => {
      alive = false;
    };
    // `user?.id` fuera por lo mismo que arriba: el login remonta la vista.
  }, [pane, days, format, minPlayers, nonceDatos]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* El detalle (clasificacion) del torneo desplegado. Cambiar de torneo
     vuelve a la clasificacion y tira las rondas del anterior. */
  useEffect(() => {
    if (!openTour) return undefined;
    let alive = true;
    setVistaTour('standings');
    setRondasTour(null);
    setRondaSel(null);
    const clave = claveCache(`/api/meta/tournaments/${openTour}`, user?.id ?? 0);
    setTourDetail(leerCache(clave) ?? null);
    api(`/api/meta/tournaments/${encodeURIComponent(openTour)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return;
        guardarCache(clave, d);
        setTourDetail(d);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [openTour, nonceDatos]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* Las RONDAS del torneo, solo si se piden: son cientos de filas y la
     mayoria de las visitas se queda en la clasificacion. */
  useEffect(() => {
    if (!openTour || vistaTour !== 'rounds' || rondasTour) return undefined;
    let alive = true;
    const clave = claveCache(`/api/meta/tournaments/${openTour}/rounds`, user?.id ?? 0);
    const enCache = leerCache(clave);
    if (enCache) {
      setRondasTour(enCache);
      setRondaSel(enCache.rounds.length - 1);
      return undefined;
    }
    api(`/api/meta/tournaments/${encodeURIComponent(openTour)}/rounds`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return;
        guardarCache(clave, d);
        setRondasTour(d);
        // Se aterriza en la ULTIMA ronda: la final es lo que se viene a ver.
        setRondaSel(d.rounds.length - 1);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [openTour, vistaTour, rondasTour, nonceDatos]);   // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Los proximos torneos, solo al abrir su pestaña. Sin cache de cliente: el
   * servidor ya guarda una hora, y aqui lo que importa es que el estado de
   * inscripcion ("quedan 3 plazas") sea de verdad y no de hace media tarde.
   */
  useEffect(() => {
    if (pane !== 'tournaments' || vistaTorneos !== 'proximos' || proximos) return undefined;
    let alive = true;
    api('/api/meta/upcoming')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (!alive) return;
        /* El servidor responde 200 con `fallo: true` cuando no pudo leer las
           fuentes: no es un error suyo, es que no se pudo preguntar a un
           tercero. Se distingue de "no hay torneos", como manda la casa. */
        if (d.fallo) setProxFallo(true);
        else setProximos(d);
      })
      .catch(() => alive && setProxFallo(true));
    return () => {
      alive = false;
    };
  }, [pane, vistaTorneos, proximos]);

  /* El ranking global de jugadores, solo con su pestaña abierta. */
  useEffect(() => {
    if (pane !== 'players') return undefined;
    let alive = true;
    const u = user?.id ?? 0;
    const ruta = `/api/meta/players?days=${days}&format=${format}&minPlayers=${minPlayers}`;
    const clave = claveCache(ruta, u);
    const enCache = leerCache(clave);
    if (enCache) setPlayersRank(enCache);
    api(ruta)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (!alive) return;
        guardarCache(clave, d);
        setPlayersRank(d);
        marcarFallo('players', false);
      })
      .catch(() => alive && marcarFallo('players', true));
    return () => {
      alive = false;
    };
  }, [pane, days, format, minPlayers, nonceDatos]);   // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Abre en la hoja del mazo la lista CONCRETA de un jugador en un torneo.
   * El agregado de inclusion de su arquetipo viaja al lado, como en openSheet:
   * es lo que separa "eleccion personal" de "lo juega todo el mundo".
   */
  const abrirListaDeTorneo = async (tourId, st) => {
    setListaBusy(st.player);
    try {
      const [r, inclusion] = await Promise.all([
        api(
          `/api/meta/tournaments/${encodeURIComponent(tourId)}/decklist` +
            `?player=${encodeURIComponent(st.player)}&lang=${lang}`
        ),
        st.deckId
          ? api(`/api/meta/archetypes/${encodeURIComponent(st.deckId)}/cards?days=${days}&format=${format}&lang=${lang}`)
              .then((x) => (x.ok ? x.json() : null))
              .catch(() => null)
          : Promise.resolve(null),
      ]);
      if (!r.ok) return;
      setSheet({ deck: await r.json(), name: st.deckName ?? st.name, inclusion });
    } finally {
      setListaBusy(null);
    }
  };

  /**
   * Se filtra ANTES de ordenar y de numerar, para que el numero de fila sea la
   * posicion dentro de lo que se esta mirando. Si numerara sobre la lista
   * completa, filtrar por tipo dejaria una tabla que empieza en el 7 y salta al
   * 12, que no es un ranking sino un residuo.
   */
  /*
   * Busqueda por nombre, insensible a acentos y mayusculas: "dusknoir" y
   * "Dusknoír" encuentran lo mismo. Filtra sobre lo que el plan alcanza a ver
   * (el servidor ya recorto la lista); no descubre arquetipos ocultos.
   */
  const [busqueda, setBusqueda] = useEstadoUrl('arq', '');
  const sinAcentos = (s) =>
    String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  const rows = useMemo(() => {
    let list = (data?.archetypes ?? []).filter((a) => a.games >= minGames);
    if (typeFilter) list = list.filter((a) => a.types?.some((t) => t.key === typeFilter));
    if (busqueda.trim()) {
      const q = sinAcentos(busqueda.trim());
      list = list.filter((a) => sinAcentos(a.name).includes(q));
    }

    const col = COLUMNS.find((c) => c.key === sort.key) ?? COLUMNS[1];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = col.value(a);
      const vb = col.value(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return cmp * dir;
    });
  }, [data, sort, typeFilter, minGames, busqueda]);

  const totalArchetypes = data?.archetypes?.length ?? 0;

  /*
   * Paginacion: diez filas por pagina.
   *
   * El ranking crecio hasta hacerse un pergamino (60+ arquetipos, y puede
   * seguir creciendo). Diez filas es lo que responde "que se juega" de un
   * vistazo; quien busca algo concreto tiene el buscador, y quien explora
   * pasa pagina. Cualquier cambio que reordene o refiltre vuelve a la
   * primera: quedarse en la pagina 4 de un resultado nuevo es quedarse
   * mirando filas que ya no son las que se pidieron.
   */
  const POR_PAGINA = 10;
  const [pagina, setPagina] = useState(1);
  useEffect(() => {
    setPagina(1);
  }, [busqueda, sort, typeFilter, minGames, days, format, minPlayers]);
  const totalPaginas = Math.max(1, Math.ceil(rows.length / POR_PAGINA));
  const paginaActual = Math.min(pagina, totalPaginas);
  const filasPagina = rows.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA);

  /**
   * Llegar a un arquetipo concreto desde el buscador global.
   *
   * NO BASTA CON DESPLEGARLO. La tabla va paginada -- diez de ciento y pico --
   * y el arquetipo buscado casi nunca cae en la primera pagina: poner
   * `openArch` y ya estaba dejaba la fila desplegada en una pagina que nadie
   * estaba viendo, asi que buscar "Dragapult Blaziken" aterrizaba en el meta
   * sin nada senalado. Se FILTRA la tabla por su nombre, que ademas escribe el
   * termino en el buscador de arquetipos: la tabla queda reducida a lo que se
   * pidio y se ve POR QUE.
   */
  useEffect(() => {
    if (!arquetipoInicial?.id) return;
    setPane('archetypes');
    if (arquetipoInicial.nombre) setBusqueda(arquetipoInicial.nombre);
    setPagina(1);
    setOpenArch(arquetipoInicial.id);
  }, [arquetipoInicial]);

  /** Mismo criterio: primer clic en la direccion util, segundo la invierte. */
  const toggleSort = (col) =>
    setSort((s) =>
      s.key === col.key
        ? { key: col.key, dir: s.dir === 'desc' ? 'asc' : 'desc' }
        : { key: col.key, dir: col.desc ? 'desc' : 'asc' }
    );

  /*
   * FILTROS POR PESTAÑA. Arriba, junto al titulo, solo quedan formato y
   * periodo: esos gobiernan las tres pestañas. Estos tres viven en la
   * pestaña a la que de verdad afectan -- tenerlos globales hacia que en
   * Torneos y en la Matriz hubiera controles que no movian nada, que es la
   * forma mas rapida de que dejen de creerse los que si mueven.
   * Se declaran una vez porque el tamaño de torneo aplica en dos pestañas.
   */
  const controlTipo = (
    <Dropdown
      label={t('type')}
      value={typeFilter}
      placeholder={t('any')}
      onChange={setTypeFilter}
      options={[
        { key: null, label: t('any') },
        ...types.map((ty) => ({ key: ty.key, label: ty.label })),
      ]}
    />
  );
  const controlTamano = (
    <Dropdown
      label={t('minTournament')}
      value={String(minPlayers)}
      porDefecto="0"
      placeholder={t('anySize')}
      onChange={(v) => setMinPlayers(Number(v ?? 0))}
      options={[
        { key: '0', label: t('anySize') },
        { key: '64', label: t('playersN', { n: 64 }) },
        { key: '128', label: t('playersN', { n: 128 }) },
        { key: '256', label: t('playersN', { n: 256 }) },
      ]}
    />
  );
  const controlMuestra = (
    <Dropdown
      label={t('minSample')}
      value={String(minGames)}
      porDefecto="20"
      placeholder={t('gamesN', { n: 20 })}
      onChange={(v) => setMinGames(Number(v ?? 20))}
      options={[
        { key: '0', label: t('minSampleAny') },
        { key: '20', label: t('gamesN', { n: 20 }) },
        { key: '50', label: t('gamesN', { n: 50 }) },
        { key: '100', label: t('gamesN', { n: 100 }) },
        { key: '250', label: t('gamesN', { n: 250 }) },
      ]}
    />
  );

  /*
   * FILTROS PLEGADOS EN EL TELÉFONO (la lección del FilterBar de Sery).
   * Apilados, Formato, Periodo, los tres de Arquetipos, el buscador y la fila
   * de orden llenaban la primera pantalla ENTERA: se entraba a una tier list y
   * no se veía ni un arquetipo. Ahora el buscador comparte fila con «Filtros»
   * y el numerito dice cuántos hay puestos sin abrir nada. En escritorio caben
   * en la banda y se quedan donde estaban.
   */
  const esMovil = useMedia('(max-width: 680px)');
  const [verFiltrosArq, setVerFiltrosArq] = useState(false);
  const nFiltrosArq =
    (typeFilter != null ? 1 : 0) + (minPlayers !== 0 ? 1 : 0) + (minGames !== 20 ? 1 : 0);

  /**
   * El cartel de "no se pudo cargar" de una pestaña, con su reintento.
   *
   * Reintentar es subir el `nonceDatos`, que es el mismo resorte que ya usaba
   * la vista para repedirlo todo cuando entra meta nuevo: no hay un segundo
   * camino de recarga que mantener. Se limpia la bandera antes de pedir para
   * que la pantalla conteste en el acto -- vuelve la pokebola -- en vez de
   * dejar el error puesto mientras se reintenta por detras.
   */
  const carteFallo = (zona) => (
    <Fallo
      titulo={t('loadFailTitle')}
      texto={t('loadFailHint')}
      textoReintentar={t('retry')}
      onReintentar={() => {
        marcarFallo(zona, false);
        setNonceDatos((n) => n + 1);
      }}
    />
  );

  if (summary && summary.tournaments === 0) {
    return (
      <div className="notice">
        <h1>{t('metaEmpty')}</h1>
        <p className="muted">{t('metaEmptyHint')}</p>
      </div>
    );
  }

  return (
    <div className="meta">
      {/*
        `div` y no `header`: dentro de `main`, un `header` sigue anunciandose
        como landmark "banner" en los navegadores de hoy, asi que la pagina
        declaraba DOS banners -- la barra de arriba y este bloque -- y quien
        navega por landmarks se encontraba la cabecera del sitio repetida a
        mitad del contenido. Esto es el titular de la vista, no una cabecera de
        documento; la clase y el aspecto no cambian.
      */}
      <div className="meta__head">
        <div>
          {/* El h1 de la portada. Es la unica pantalla que se lleva el titulo
              principal del sitio, y dice de que va: el meta, no la marca. */}
          <h1>{t('metaTitle')}</h1>
          {/*
            La frase con las palabras que se buscan ("tier list", "Pokémon TCG
            Live", "torneos"), VISIBLE y no solo en el <head>: el robot pesa el
            contenido renderizado, y esta línea es la única de la portada que
            dice que este meta es el mismo del TCG Live.
          */}
          <p className="meta__lema">{t('metaTagline')}</p>
          {/*
            * Las cifras son EL argumento de esta pantalla ("no es una tabla
            * inventada: son 27 torneos y 7.777 partidas de verdad") y ademas
            * cambian con cada ingesta. El punto que late y los numeros que
            * cuentan hasta su valor lo dicen sin una sola palabra de mas; el
            * "actualizado hace X" le pone fecha a la promesa.
            *
            * SE PINTA SIEMPRE, aunque no haya datos todavia. Colgado de
            * `summary &&`, este parrafo no existia hasta que contestaba la API
            * -- casi un segundo -- y al aparecer empujaba hacia abajo todo lo
            * que tenia debajo: cabecera, pestañas y tabla. Medido en
            * produccion, ahi estaban los nueve saltos de maquetacion que
            * dejaban el CLS en 0,165 (el limite es 0,10).
            *
            * La cura no es un `min-height` a ojo, que se descuadra en cuanto
            * cambie el texto: es que la ESTRUCTURA no cambie. El alto de este
            * bloque no lo decide lo largo del texto sino cuantos hijos tiene
            * -- una linea en escritorio, cuatro en el telefono, donde cada
            * hijo cae en la suya --, asi que pintando los mismos cuatro con
            * un guion de relleno mientras se espera, el hueco ya esta
            * reservado y nada se mueve cuando llegan los numeros.
            */}
            {/*
              EL PULSO DEL META: un marcador, no una frase.

              Esto era una linea corrida -- "EN VIVO · 62 torneos · 6965
              jugadores · 17.588 partidas · actualizado hace 10 h" -- y las
              cifras son EL argumento de la pantalla ("no es una tabla
              inventada"). Escritas al hilo del texto, con su palabra pegada
              detras y separadas por puntos, habia que LEERLAS para sacar el
              dato; en el telefono ademas partian en tres o cuatro renglones y
              el argumento se deshacia del todo.

              De marcador se leen de un vistazo: cifra grande arriba, rotulo
              corto debajo, celdas del mismo ancho separadas por una linea. Es
              la forma que ya tiene cualquier tanteador de liga, y esa es
              justamente la lectura que se busca -- el estado de la temporada.

              EL HUECO SIGUE RESERVADO DESDE EL PRIMER FOTOGRAMA. Este bloque
              es el que dejo el CLS de la portada en 0,165: colgado de
              `summary &&` no existia hasta que contestaba la API y al aparecer
              empujaba cabecera, pestañas y tabla. La cura era que la
              ESTRUCTURA no cambie, y aqui se conserva entera: las cuatro
              celdas se pintan siempre -- con un guion mientras no hay dato --
              y cada cifra sigue siendo `CifraViva`, que reserva el ancho de su
              valor final con un medidor invisible. Nada de `min-height` a ojo.
            */}
            <div className="pulso">
              <button
                type="button"
                className="pulso__cel pulso__cel--ir"
                onClick={() => irA('tournaments')}
                aria-label={summary ? `${cifra(summary.tournaments, lang)} ${t('metaTournaments')} · ${t('metaGoTournaments')}` : t('metaGoTournaments')}
              >
                <span className="pulso__n">
                  {summary ? <CifraViva n={summary.tournaments} lang={lang} /> : '—'}
                </span>
                <span className="pulso__rot">
                  {t('metaTournaments')}
                  <span className="pulso__flecha" aria-hidden="true">→</span>
                </span>
              </button>
              <button
                type="button"
                className="pulso__cel pulso__cel--ir"
                onClick={() => irA('players')}
                aria-label={summary ? `${cifra(summary.players, lang)} ${t('metaPlayers')} · ${t('metaGoPlayers')}` : t('metaGoPlayers')}
              >
                <span className="pulso__n">
                  {summary ? <CifraViva n={summary.players} lang={lang} /> : '—'}
                </span>
                <span className="pulso__rot">
                  {t('metaPlayers')}
                  <span className="pulso__flecha" aria-hidden="true">→</span>
                </span>
              </button>
              <button
                type="button"
                className="pulso__cel pulso__cel--ir"
                onClick={() => irA('matchups')}
                aria-label={summary ? `${cifra(summary.pairings, lang)} ${t('metaGames')} · ${t('metaGoMatchups')}` : t('metaGoMatchups')}
              >
                <span className="pulso__n">
                  {summary ? <CifraViva n={summary.pairings} lang={lang} /> : '—'}
                </span>
                <span className="pulso__rot">
                  {t('metaGames')}
                  <span className="pulso__flecha" aria-hidden="true">→</span>
                </span>
              </button>
              {/*
                La cuarta celda no es una cifra sino el ESTADO: el distintivo
                que late y, debajo, desde cuando. Va en la misma banda porque
                responde a la misma pregunta que las otras tres -- "¿me puedo
                fiar de esto?" --, y una tabla del meta sin fecha es media
                promesa.

                Con retraso aplicado (plan gratis) el distintivo dice la verdad
                en ambar y lleva al aviso de Pro: prometer EN VIVO sobre una
                tabla envejecida seria mentir. `delayedDays` lo decide el
                servidor, que es quien sabe si hay historia suficiente.
              */}
              <div className="pulso__cel pulso__cel--estado">
                <span className="pulso__vivoFila">
                {data?.delayedDays > 0 ? (
                  <button
                    type="button"
                    className="vivo vivo--retraso"
                    title={t('metaDelayedHint')}
                    onClick={() => setGate({ feature: 'meta' })}
                  >
                    <span className="vivo__punto" aria-hidden="true" />
                    {t('metaDelayed', { n: data.delayedDays })}
                  </button>
                ) : (
                  <span className="vivo">
                    <span className="vivo__punto" aria-hidden="true" />
                    {t('metaLive')}
                  </span>
                )}
                {/*
                  "¿De donde salen estos numeros?" se pregunta mirando el dato,
                  asi que la respuesta cuelga del distintivo y no de un enlace
                  suelto debajo de la banda (que se comia una linea entera de la
                  portada). Posado: el resumen en una frase. Pulsado: la
                  metodologia completa.
                */}
                <Pista texto={t('metaHowHint')}>
                  <button
                    type="button"
                    className="pulso__como"
                    aria-label={t('methodologyLink')}
                    onClick={() => window.dispatchEvent(new CustomEvent('ptcg:abrir-metodologia'))}
                  >
                    ?
                  </button>
                </Pista>
                </span>
                {/* Ocupa su sitio desde el primer pintado: con el retraso del
                    plan gratis no hay "hace X" que dar, y ahi el hueco lo
                    llena el propio distintivo ambar, que es mas ancho. */}
                <span className="pulso__rot pulso__rot--hace">
                  {data?.delayedDays > 0
                    ? ' '
                    : metaEdadMs != null
                      ? t('metaUpdatedAgo', { t: haceCuanto(metaEdadMs, t) })
                      : '—'}
                </span>
              </div>
            </div>
        </div>
        <div className="meta__controls">
          {formats.length > 1 ? (
            <Dropdown
              label={t('format')}
              value={format}
              placeholder="Standard"
              onChange={(v) => setFormat(v ?? 'STANDARD')}
              options={[
                ...formats.map((f) => ({
                  key: f.format,
                  label: `${f.format.charAt(0) + f.format.slice(1).toLowerCase()} (${f.tournaments})`,
                })),
                { key: 'ALL', label: t('allFormats') },
              ]}
            />
          ) : (
            /* Con un solo formato ingestado no hay nada que elegir, pero
               callarselo dejaba al visitante sin saber QUE meta mira: todo lo
               de esta pestaña es de este formato. Mismo esqueleto visual que
               los desplegables vecinos, como dato y no como control. */
            <div className="meta__formatoFijo">
              <span className="dropdown__label">{t('format')}</span>
              <span className="meta__formatoValor">
                {(() => {
                  const f = formats[0]?.format ?? format;
                  return f.charAt(0) + f.slice(1).toLowerCase();
                })()}
              </span>
            </div>
          )}
          {/* La ventana es libre para todos desde jul-2026 (ver plans.js): el
              servidor sirve cualquier periodo y aquí no queda puerta. Si el
              meta vuelve a ser de pago, este selector debe volver a explicar
              el límite al elegir en vez de fingir que se aplicó. */}
          <Dropdown
            label={t('metaWindow')}
            value={String(days)}
            placeholder="7"
            onChange={(v) => setDays(Number(v ?? 7))}
            options={[
              { key: '7', label: t('days7') },
              { key: '30', label: t('days30') },
              { key: '90', label: t('days90') },
              { key: '0', label: t('daysAll') },
            ]}
          />
        </div>
      </div>

      {/*
        UNA SOLA BANDA: las pestañas a la izquierda y los filtros DE ESA
        pestaña a su derecha, bajo la misma raya. Antes eran dos filas de
        controles seguidas -- pestañas y luego tres desplegables sueltos --
        y con las de la nav arriba y las globales del cabecero sumaban cuatro
        franjas de botones que se leian como una ensalada. Juntos ademas se
        dice sin palabras a QUE pestaña pertenece cada filtro.
      */}
      <div
        className={`meta__banda${bandaPegada ? ' is-pegada' : ''}`}
        ref={bandaRef}
      >
        <Subtabs
          label={t('metaTitle')}
          value={pane}
          onChange={setPane}
          items={[
            { key: 'archetypes', label: t('paneArchetypes') },
            /* "Próximos" NO es una subpestaña propia: vive DENTRO de Torneos,
               que es donde pertenece. Un torneo que viene y uno que ya se jugó
               son el mismo objeto en dos momentos, no dos secciones. */
            { key: 'tournaments', label: t('paneTournaments') },
            { key: 'players', label: t('panePlayers') },
            { key: 'matchups', label: t('paneMatchups'), short: t('paneMatchupsShort') },
          ]}
        />

        {/* Los filtros de cada pestaña, FUERA de su ternaria de contenido: si
            un filtro estricto deja la tabla vacia, el control para aflojarlo
            tiene que seguir a mano. */}
        {pane === 'archetypes' && !esMovil && (
          <div className="meta__controls meta__controls--banda">
            {controlTipo}
            {controlTamano}
            {controlMuestra}
          </div>
        )}
        {/* El tamaño mínimo filtra la base de torneos YA JUGADOS. En
            "Próximos" no aplica: nadie sabe todavía cuánta gente irá. */}
        {((pane === 'tournaments' && vistaTorneos === 'pasados') || pane === 'players') && (
          <div className="meta__controls meta__controls--banda">{controlTamano}</div>
        )}

        {/*
          QUE ESTAS MIRANDO, cuando la cabecera ya no se ve. Formato y periodo
          gobiernan las cuatro pestañas, asi que viajan con ellas. Van los dos
          que MANDAN sobre la cifra, no todas las cifras: el recuento de
          torneos y partidas es la pregunta "¿cuantos datos hay?", que se
          contesta una vez arriba; esta linea contesta "¿de que y de cuando?",
          que es la que se olvida a mitad de una tabla larga.

          `inert` + `aria-hidden` mientras esta plegada, como los dos modos del
          formulario de partidas: sigue en el DOM para poder animarla, pero un
          lector no debe encontrarse el contexto dos veces (aqui y en la
          cabecera) cuando las dos cosas se ven a la vez.
        */}
        <p
          className="meta__contexto"
          inert={!bandaPegada}
          aria-hidden={!bandaPegada}
        >
          {/* UN SOLO hijo, y dentro todo lo demás: la rejilla 0fr→1fr sólo
              encoge la primera fila, así que con los span sueltos colgando del
              padre el resto se quedaba a la vista en reposo. */}
          <span className="meta__contextoInner">
          <span className="meta__contextoDato">
            {format === 'ALL'
              ? t('allFormats')
              : (() => {
                  const f = formats[0]?.format ?? format;
                  return f.charAt(0) + f.slice(1).toLowerCase();
                })()}
          </span>
          <span className="meta__contextoSep" aria-hidden="true"> · </span>
          <span className="meta__contextoDato">
            {t({ 7: 'days7', 30: 'days30', 90: 'days90', 0: 'daysAll' }[days] ?? 'days7')}
          </span>
          {metaEdadMs != null && !(data?.delayedDays > 0) && (
            <>
              <span className="meta__contextoSep" aria-hidden="true"> · </span>
              <span className="muted">
                {t('metaUpdatedAgo', { t: haceCuanto(metaEdadMs, t) })}
              </span>
            </>
          )}
          </span>
        </p>
      </div>

      {/* El fallo sólo tapa la pestaña si NO hay nada que enseñar: con la
          tabla de la visita anterior en pantalla (caché), una recarga que se
          cae no debe borrarla -- dato viejo es mejor que nada. */}
      {pane === 'archetypes' && (fallos.archetypes && !data ? (
        carteFallo('archetypes')
      ) : !data ? (
        <Cargando texto={t('loading')} />
      ) : rows.length === 0 && !busqueda.trim() ? (
        <>
          {/* Un filtro estricto puede dejar la tabla vacía; en el teléfono los
              filtros viven junto al buscador, que aquí no se pinta, así que se
              quedan a la vista para poder aflojarlos. */}
          {esMovil && nFiltrosArq > 0 && (
            <div className="meta__controls meta__controls--banda meta__filtrosmovil">
              {controlTipo}
              {controlTamano}
              {controlMuestra}
            </div>
          )}
          <Vacio tipo="pokebola" texto={t('metaNoData')} />
        </>
      ) : (
        <>
        {/* El buscador vive FUERA del "sin resultados": si una busqueda deja
            cero filas, el campo tiene que seguir ahi para poder corregirla. */}
        <div className="meta__buscarfila">
          <div className="meta__buscarcaja">
            <input
              className="meta__buscar"
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={t('searchArchetype')}
              aria-label={t('searchArchetype')}
            />
            {esMovil && (
              <button
                type="button"
                className="btn btn--ghost filtrosbtn"
                aria-expanded={verFiltrosArq}
                aria-controls="meta-filtros-arq"
                onClick={() => setVerFiltrosArq((v) => !v)}
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="filtrosbtn__icono">
                  <path d="M4 6h16M7 12h10m-7 6h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
                {t('filters')}
                {nFiltrosArq > 0 && <span className="filtrosbtn__n">{nFiltrosArq}</span>}
              </button>
            )}
          </div>
          {esMovil && verFiltrosArq && (
            <div className="meta__controls meta__controls--banda meta__filtrosmovil" id="meta-filtros-arq">
              {controlTipo}
              {controlTamano}
              {controlMuestra}
            </div>
          )}
          <p className="muted meta__shown">
            {t('archetypesShown', { n: rows.length, total: totalArchetypes })}
          </p>
        </div>

        {rows.length === 0 ? (
          <p className="muted center">{t('archSearchEmpty', { q: busqueda.trim() })}</p>
        ) : (
        <>

        {/* Orden para móvil. En escritorio se ordena pinchando la cabecera, pero
            ahí la tabla se convierte en tarjetas y las cabeceras desaparecen, así
            que el criterio necesita un control propio. El CSS decide cuál de los
            dos se ve; el estado de orden es el mismo para ambos. */}
        <div className="meta__sortbar" role="group" aria-label={t('sortBy')}>
          <span className="meta__sortbarLabel">{t('sortBy')}</span>
          {/* El desvanecido del borde avisa de que hay mas criterios fuera de
              vista, y SOLO cuando de verdad los hay: mismo patron medido que
              las pastillas de mazos (hay-mas). Sin el, "Infravalorado" y
              "Top 8" quedaban fuera de pantalla sin ninguna seña. */}
          <div className={`meta__sortchips${chipsHayMas ? ' hay-mas' : ''}`} ref={chipsRef}>
            {COLUMNS.filter((c) => c.key !== 'name').map((col) => {
              const active = sort.key === col.key;
              const chip = (
                <button
                  type="button"
                  className={`meta__chip${active ? ' is-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => toggleSort(col)}
                >
                  {t(col.labelKey)}
                  {active && (
                    <span className="meta__chipdir" aria-hidden="true">
                      {sort.dir === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              );
              /*
               * "Infravalorado" es una cifra que nos inventamos nosotros y hay
               * que explicarla. En escritorio se explicaba con un `title`; en
               * el telefono -- que es esta fila de chips -- no se explicaba en
               * NINGUN sitio, porque un `title` sin raton no existe. Con la
               * pista, tocar el chip cuenta que mide.
               */
              return col.hintKey ? (
                <Pista key={col.key} texto={t(col.hintKey)} lado="abajo">
                  {chip}
                </Pista>
              ) : (
                <Fragment key={col.key}>{chip}</Fragment>
              );
            })}
          </div>
        </div>
        <table className={`meta__table meta__arqtable${refrescando ? ' is-refrescando' : ''}`}>
          <thead>
            <tr>
              <th className="meta__rankh" scope="col">{t('rank')}</th>
              {COLUMNS.map((col) => {
                const active = sort.key === col.key;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={col.align === 'num' ? 'num' : undefined}
                    // aria-sort es lo que anuncia el orden a un lector de
                    // pantalla; sin el, la flecha solo existe para quien la ve.
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <Pista texto={col.hintKey ? t(col.hintKey) : null} lado="abajo">
                      <button
                        type="button"
                        className={`meta__sortbtn${active ? ' is-active' : ''}`}
                        onClick={() => toggleSort(col)}
                        aria-label={`${t('sortBy')} ${t(col.labelKey)}`}
                      >
                        <span>{t(col.labelKey)}</span>
                        <svg className="meta__sortarrow" viewBox="0 0 10 6" aria-hidden="true">
                          <path d="M1 1.5 5 5 9 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                        </svg>
                      </button>
                    </Pista>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filasPagina.map((a, i) => {
              const isOpen = openArch === a.id;
              const isClosing = closingArch === a.id;
              const edge = edgeScore(a);
              return (
                <Fragment key={a.id}>
                  {/*
                    La fila NO lleva `aria-expanded`. Un `row` solo lo admite
                    dentro de un `treegrid`, y esta es una tabla normal: axe lo
                    marcaba diez veces por pagina (`aria-conditional-attr`) y
                    era el unico fallo de accesibilidad que quedaba.
                    El estado vive ahora en el boton del chevron, que ademas es
                    la unica forma de llegar aqui con el teclado -- ver abajo.
                    El clic en la fila entera se queda: es comodo con raton.
                  */}
                  <tr
                    className={`meta__row${isOpen ? ' is-open' : ''}`}
                    style={retraso(i)}
                    onClick={() => toggle(a.id)}
                  >
                    {/* La posicion es del RANKING completo, no de la pagina:
                        en la pagina 2 se sigue leyendo 11, 12, 13… */}
                    <td className="meta__rank">{(paginaActual - 1) * POR_PAGINA + i + 1}</td>
                    <td>
                      {/* El Pokemon insignia de fondo, como en las tarjetas de
                          emparejamientos: la cara se reconoce antes que el
                          nombre. En movil abarca la tarjeta entera; en
                          escritorio el CSS la confina a la columna del nombre
                          para no pisar las cifras. */}
                      {cardArt(a.portada) && (
                        <span
                          className="rowart meta__rowart"
                          style={{ '--art': `url(${cardArt(a.portada)})` }}
                          aria-hidden="true"
                        />
                      )}
                      {/*
                        EL CHEVRON ES UN BOTON, y no adorno.
                        La fila se desplegaba solo con `onClick` en el <tr>:
                        `tabIndex -1`, sin rol y sin nada enfocable dentro, o
                        sea que CON TECLADO NO HABIA FORMA de abrir un
                        arquetipo -- ni sus cartas, ni sus emparejamientos, ni
                        los botones de ver la lista o registrar partida. Es un
                        fallo de teclado que ninguna herramienta automatica
                        detecta, porque no sabe que ese `onClick` era la unica
                        entrada.
                        Lleva el nombre del arquetipo en la etiqueta: diez
                        botones que solo digan "ver detalle" no se distinguen
                        al recorrerlos con un lector.
                        `aria-controls` solo cuando esta abierto: apuntar a un
                        id que no existe es otro fallo de ARIA.
                      */}
                      <button
                        type="button"
                        className="meta__chevronBtn"
                        aria-expanded={isOpen}
                        aria-controls={isOpen ? `arqdet-${a.id}` : undefined}
                        aria-label={t('archDetailToggle', { name: a.name })}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(a.id);
                        }}
                      >
                        <svg className="meta__chevron" viewBox="0 0 12 8" aria-hidden="true">
                          <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
                        </svg>
                      </button>
                      {/* Nombre ARRIBA y nucleo en sprites DEBAJO, como en el
                          catalogo de mazos publicos: un arquetipo se reconoce
                          por las caras antes que por el nombre. `sinCuenta`
                          porque las copias de UNA lista representativa se
                          leerian como un dato agregado que no es. */}
                      <span className="meta__arqceld">
                        <span className="meta__arqnombre">{a.name}</span>
                        {a.nucleo?.length > 0 && <Nucleo cartas={a.nucleo} t={t} sinCuenta />}
                      </span>
                    </td>
                    <td className="meta__tipos" data-label={t('type')}>
                      {a.types?.length > 0 && (
                        <span className="typeicons">
                          {a.types.map((ty) => (
                            <TypeIcon
                              key={ty.key}
                              type={ty.key}
                              share={ty.share}
                              label={types.find((x) => x.key === ty.key)?.label}
                            />
                          ))}
                        </span>
                      )}
                    </td>
                    {/* `data-label` no se usa en escritorio: lo pinta el CSS de
                        móvil, donde la tabla se convierte en tarjetas y las
                        cabeceras dejan de existir. Es lo que permite tener un
                        solo marcado para las dos formas. */}
                    <td className="num" data-label={t('share')}>{pct(a.share)}</td>
                    <td className="num" data-label={t('winRateShort')}>
                      <span style={cellStyle(a.winRate)} className="meta__wr">{pct(a.winRate)}</span>
                    </td>
                    <td className={`num meta__edge${edge > 0 ? ' is-pos' : ''}`} data-label={t('edge')}>
                      {edge > 0 ? '+' : ''}{(edge * 100).toFixed(1)}
                    </td>
                    <td className="num" data-label={t('conversion')} title={`${a.top8} / ${a.players}`}>
                      {pct(a.conversion, 0)}
                    </td>
                    <td className="num muted" data-label={t('games')}>{a.games}</td>
                  </tr>

                  {(isOpen || isClosing) && (
                    <tr className="meta__detailrow" id={`arqdet-${a.id}`}>
                      <td colSpan={COLUMNS.length + 1}>
                        {/* La rejilla de una sola fila pasa de 0fr a 1fr: es la
                            forma de animar hasta una altura desconocida sin
                            fijar un max-height a ojo que luego recorte. */}
                        <div className={`meta__expand${isClosing ? ' is-closing' : ''}`}>
                          <div className="meta__expandInner">
                            <div className="meta__expandBar">
                              <button
                                type="button"
                                className="btn"
                                disabled={sheetBusy}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSheet(a.id, a.name);
                                }}
                              >
                                {sheetBusy ? t('loading') : t('viewDeckSheet')}
                              </button>
                              <span className="muted">{t('deckSheetHint')}</span>
                              <TendenciaArquetipo trend={archTrend} t={t} lang={lang} />
                            </div>
                            <ArchetypeCards
                              cards={cards}
                              matchups={archMatchups}
                              name={a.name}
                              t={t}
                              onOpenCard={onOpenCard}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        <Paginador pagina={paginaActual} total={totalPaginas} onPagina={setPagina} />

        {truncated && (
          <div className="meta__truncated">
            <span>{t('showingOf', { n: truncated.shown, total: truncated.total })}</span>
            <button type="button" onClick={() => setGate({ feature: 'meta' })}>
              {user ? t('seeAllPro') : t('seeAllProTrial')}
            </button>
          </div>
        )}
        </>
        )}
        </>
      ))}

      {/* ------------------------------------------------------------------
          PROXIMOS TORNEOS. La unica pestaña del meta que mira hacia delante:
          las otras cuatro cuentan lo que ya paso, esta dice a que te puedes
          apuntar. Por eso el estado de inscripcion manda en la tarjeta.
          ------------------------------------------------------------------ */}
      {/* Lo que viene o lo que ya pasó: dos momentos del mismo objeto, no dos
          secciones. Mismos chips que Clasificación|Rondas dentro de un torneo,
          que es la misma figura un nivel más abajo. */}
      {/*
        UNA SOLA BARRA DE CONTROLES, y fuera de lo que depende de los datos.

        Estaban en dos filas -- momento arriba, region debajo -- y ademas la de
        region vivia DENTRO del bloque que espera a la API. Dos saltos por el
        precio de uno: la lista empezaba mas abajo en "Próximos" que en "Ya
        jugados" porque habia una fila mas, y encima esa fila no existia
        mientras cargaba, asi que al llegar los datos todo bajaba de golpe. Lo
        segundo es justo lo que la regla de la casa prohibe: nada que llegue
        tarde puede mover lo que ya esta pintado.

        Juntos en una fila, los dos grupos son lo mismo -- "que estoy mirando"
        -- y la lista arranca a la misma altura se elija lo que se elija.
      */}
      {pane === 'tournaments' && (
        <div className="prox__barra">
          {/* SEGMENTO, no chips: elegir entre dos listas es una decisión de
              otro nivel que filtrar la que ya miras, y pintadas con la misma
              píldora que las regiones se leían como cuatro, cinco, seis
              filtros iguales (Hick: lo que manda se distingue a la vista). */}
          <div className="segmento" role="group" aria-label={t('paneTournaments')}>
            {[
              ['proximos', t('paneUpcoming')],
              ['pasados', t('tourPasados')],
            ].map(([k, etiqueta]) => (
              <button
                key={k}
                type="button"
                className={`segmento__op${vistaTorneos === k ? ' is-active' : ''}`}
                aria-pressed={vistaTorneos === k}
                /* Arriba al cambiar, por lo mismo que las subpestañas (ver
                   Subtabs.jsx): "próximos" y "ya jugados" son dos listas
                   distintas, y heredar el scroll de la otra aterriza a mitad
                   de una lista que nadie ha recorrido. Los chips de al lado NO
                   lo hacen: filtran la lista que ya estás mirando. */
                onClick={() => {
                  if (vistaTorneos !== k) window.scrollTo({ top: 0 });
                  setVistaTorneos(k);
                }}
              >
                {etiqueta}
              </button>
            ))}
          </div>

          {vistaTorneos === 'proximos' && (
            <>
              <span className="rail__raya" aria-hidden="true" />
              <div className="tour__vistas" role="group" aria-label={t('upcomingRegion')}>
                {[
                  ['latam', t('upcomingLatam')],
                  ['cl', t('upcomingChile')],
                  ['online', t('upcomingOnline')],
                  ['todos', t('upcomingTodos')],
                ].map(([k, etiqueta]) => (
                  <button
                    key={k}
                    type="button"
                    className={`meta__chip${region === k ? ' is-active' : ''}`}
                    aria-pressed={region === k}
                    onClick={() => setRegion(k)}
                  >
                    {etiqueta}
                  </button>
                ))}
              </div>
              {/* "Inscripcion abierta" NO es una deduccion nuestra: es el
                  estado que publica el propio Limitless en su boton de
                  apuntarse. Por eso se puede prometer sin mentir. */}
              {/* Casilla y no chip: es un sí/no, y la casilla es el control que
                  todo el mundo reconoce para eso — la misma de «solo la mejor
                  de cada arquetipo» en el catálogo de mazos. */}
              <label className="mpub__check prox__soloAbiertos">
                <input
                  type="checkbox"
                  checked={soloAbiertos}
                  onChange={(e) => setSoloAbiertos(e.target.checked)}
                />
                <span>{t('upcomingSoloAbiertos')}</span>
              </label>
            </>
          )}
        </div>
      )}

      {pane === 'tournaments' && vistaTorneos === 'proximos' && (
        proxFallo ? (
          <Fallo
            texto={t('upcomingFallo')}
            onReintentar={() => {
              setProxFallo(false);
              setProximos(null);
            }}
          />
        ) : !proximos ? (
          <Cargando texto={t('loading')} />
        ) : (
          (() => {
            const REGIONES = {
              latam: (x) => LATAM_PAISES.includes(x.pais),
              cl: (x) => x.pais === 'CL',
              online: (x) => x.fuente === 'comunidad',
              todos: () => true,
            };
            const lista = proximos.tournaments
              .filter(REGIONES[region] ?? REGIONES.todos)
              .filter((x) => !soloAbiertos || x.inscripcionAbierta);

            return (
              <div className="prox">
                {lista.length === 0 ? (
                  <Vacio tipo="pokebola" titulo={t('upcomingVacio')} texto={t('upcomingVacioHint')}>
                    <div className="vacio__acciones">
                      <button type="button" className="btn" onClick={() => { setRegion('todos'); setSoloAbiertos(false); }}>
                        {t('upcomingTodos')}
                      </button>
                    </div>
                  </Vacio>
                ) : (
                  <ul className="prox__lista">
                    {lista.map((x) => (
                      <li key={x.id} className={`prox__item${x.pais === 'CL' ? ' is-casa' : ''}`}>
                        <div className="prox__fecha">
                          <span className="prox__dia">{diaDe(x.fecha, lang)}</span>
                          <span className="prox__mes">{mesDe(x.fecha, lang)}</span>
                        </div>
                        <div className="prox__cuerpo">
                          <p className="prox__nombre">
                            {x.pais && (
                              <Bandera cc={x.pais} />
                            )}
                            {x.url ? (
                              <a href={x.url} target="_blank" rel="noopener noreferrer">{x.nombre}</a>
                            ) : (
                              x.nombre
                            )}
                          </p>
                          <p className="muted prox__meta">
                            {x.fuente === 'oficial' ? (
                              <span className="prox__sello">{t('upcomingOficial')}</span>
                            ) : (
                              <span className="prox__sello prox__sello--com">{x.plataforma ?? t('upcomingComunidad')}</span>
                            )}
                            {(x.formato || x.formatoJuego) && (
                              <> · {(x.formato || x.formatoJuego).replace(/-/g, ' ')}</>
                            )}
                            {x.organizador && <> · {x.organizador}</>}
                            {x.inscritos != null && (
                              <> · {x.cupo ? `${x.inscritos}/${x.cupo}` : t('upcomingInscritos', { n: x.inscritos })}</>
                            )}
                          </p>
                        </div>
                        {x.urlInscripcion && (
                          <a
                            className={`btn${x.inscripcionAbierta ? '' : ' btn--ghost'} prox__cta`}
                            href={x.urlInscripcion}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {x.inscripcionAbierta ? t('upcomingApuntarse') : t('upcomingVerEvento')}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {/* De donde sale esto. Un calendario que se lee de otra web
                    tiene que decirlo: es su dato, no nuestro. */}
                <p className="muted prox__fuente">{t('upcomingFuente')}</p>
              </div>
            );
          })()
        )
      )}

      {pane === 'tournaments' && vistaTorneos === 'pasados' && (fallos.tournaments && !tourneys ? (
        carteFallo('tournaments')
      ) : !tourneys ? (
        <Cargando texto={t('loading')} />
      ) : (tourneys.tournaments ?? []).length === 0 ? (
        <Vacio tipo="pokebola" texto={t('metaNoData')} />
      ) : (
        <>
        <ul className="tourlist">
          {tourneys.tournaments
            .slice((tourPagina - 1) * POR_PAGINA, tourPagina * POR_PAGINA)
            .map((tor) => {
            const abierto = openTour === tor.id;
            /*
             * La fecha se pinta con la MISMA vara con la que el servidor
             * ordena: el dia del calendario que registro Limitless (los diez
             * primeros caracteres). Convertirla a hora local hacia que un
             * torneo del 26 a la 01:00Z se pintara como "25" y la lista
             * pareciera desordenada sin estarlo. El T12:00:00 evita que el
             * parseo UTC vuelva a correr el dia.
             */
            /* Mismo taco de dos pisos que en "Próximos" -- día grande arriba,
               mes debajo -- y con los mismos ayudantes. Las dos listas se
               alternan con un chip en la misma pestaña: si la fecha se lee de
               una forma en una y de otra en la otra, el cambio parece un salto
               a otra pantalla. */
            return (
              <li key={tor.id} className={`tour${abierto ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="tour__cab"
                  aria-expanded={abierto}
                  onClick={() => setOpenTour(abierto ? null : tor.id)}
                >
                  <span className="tour__fecha prox__fecha">
                    <span className="prox__dia">{diaDe(tor.date)}</span>
                    <span className="prox__mes">{mesDe(tor.date, lang)}</span>
                  </span>
                  <span className="tour__nombre">{tor.name}</span>
                  <span className="tour__datos muted">
                    {/*
                      EL FORMATO SOLO CUANDO DESAMBIGUA.

                      El brief pide un distintivo de formato en la cabecera del
                      torneo, y con el filtro en Standard -- que es lo normal --
                      seria la misma palabra repetida en las diez filas: una
                      columna que dice lo mismo siempre no informa, solo ocupa.
                      Con "Todos los formatos" elegido si distingue una fila de
                      otra, y ahi aparece.
                    */}
                    {format === 'ALL' && tor.format && (
                      <>
                        <span className="tour__formato">
                          {tor.format.charAt(0) + tor.format.slice(1).toLowerCase()}
                        </span>
                        {' · '}
                      </>
                    )}
                    {t('tourPlayers', { n: tor.players })}
                    {tor.decklists > 0 && <> · {t('tourLists', { n: tor.decklists })}</>}
                  </span>
                  {tor.winner && (
                    <span className="tour__campeon" title={t('tourWinner')}>
                      <span aria-hidden="true">🏆</span> {tor.winner.name}
                      {tor.winner.deckName && (
                        <span className="tour__campeonMazo"> · {tor.winner.deckName}</span>
                      )}
                    </span>
                  )}
                  <svg className="meta__chevron tour__chevron" viewBox="0 0 12 8" aria-hidden="true">
                    <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
                  </svg>
                </button>

                {abierto && (
                  <div className="tour__detalle">
                    {!tourDetail || tourDetail.id !== tor.id ? (
                      <Cargando texto={t('loading')} />
                    ) : (
                      <>
                        {/* Clasificacion o rondas: las rondas son la otra
                            mitad del torneo (quien jugo contra quien) y
                            estaban ingeridas sin poderse mirar. */}
                        <div className="tour__vistas" role="group">
                          {[
                            { key: 'standings', label: t('tourStandingsTab') },
                            { key: 'rounds', label: t('tourRoundsTab') },
                          ].map((v) => (
                            <button
                              key={v.key}
                              type="button"
                              className={`meta__chip${vistaTour === v.key ? ' is-active' : ''}`}
                              aria-pressed={vistaTour === v.key}
                              onClick={() => setVistaTour(v.key)}
                            >
                              {v.label}
                            </button>
                          ))}
                        </div>

                        {vistaTour === 'rounds' ? (
                          !rondasTour ? (
                            <Cargando texto={t('loading')} />
                          ) : (
                            <div className="rondas">
                              <div className="rondas__selector">
                                {rondasTour.rounds.map((r, i) => (
                                  <button
                                    key={`${r.phase}-${r.round}`}
                                    type="button"
                                    className={`meta__chip${rondaSel === i ? ' is-active' : ''}`}
                                    aria-pressed={rondaSel === i}
                                    onClick={() => setRondaSel(i)}
                                  >
                                    {t(r.phase > 1 ? 'topRoundShort' : 'roundShort', { n: r.round })}
                                  </button>
                                ))}
                              </div>
                              <ul className="rondas__lista">
                                {(rondasTour.rounds[rondaSel]?.pairings ?? []).map((par, i) => {
                                  const empate = par.winner === '0';
                                  const sinRes = par.winner === '-1' || par.winner == null;
                                  const gana1 = !empate && !sinRes && par.winner === par.player1.id;
                                  const gana2 = !empate && !sinRes && par.player2 && par.winner === par.player2.id;
                                  const lado = (p, gana) => (
                                    <span className={`rondas__lado${gana ? ' is-ganador' : ''}`}>
                                      <button
                                        type="button"
                                        className="tour__jugador"
                                        onClick={() => setPerfil(p.id)}
                                      >
                                        {p.name}
                                      </button>
                                      {p.deck && <span className="muted rondas__mazo"> · {p.deck}</span>}
                                    </span>
                                  );
                                  return (
                                    <li key={i} className="rondas__par">
                                      {lado(par.player1, gana1)}
                                      <span className="rondas__vs muted">
                                        {empate ? t('tourTie') : 'vs'}
                                      </span>
                                      {par.player2 ? (
                                        lado(par.player2, gana2)
                                      ) : (
                                        <span className="muted rondas__lado">{t('tourBye')}</span>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )
                        ) : (
                        <table className="meta__table tour__tabla">
                          <thead>
                            <tr>
                              <th scope="col" className="meta__rankh">{t('rank')}</th>
                              <th scope="col">{t('tourPlayer')}</th>
                              <th scope="col" className="num">{t('tourRecord')}</th>
                              <th scope="col">{t('tourDeck')}</th>
                              <th scope="col" aria-hidden="true" />
                            </tr>
                          </thead>
                          <tbody>
                            {tourDetail.standings.map((st) => (
                              <tr
                                key={st.player}
                                className="tour__fila jrow"
                                onClick={() => setPerfil(st.player)}
                              >
                                {/*
                                  AQUI el puesto SI es un logro: `st.placing` es
                                  como terminó el torneo, no la fila en que ha
                                  caído. Por eso el podio se marca aquí y no en
                                  las otras dos tablas con numerito -- la de
                                  arquetipos y el ranking de jugadores son
                                  ORDENABLES por cualquier columna, asi que su
                                  "1" es un indice de fila que cambia al pulsar
                                  una cabecera. Una medalla de oro ahi diria
                                  "el mejor" cuando lo que dice el dato es "el
                                  primero segun lo que acabas de ordenar".
                                */}
                                <td className="meta__rank">
                                  {st.placing >= 1 && st.placing <= 3 ? (
                                    /* El numero VA DENTRO del span, no lo pinta
                                       un `content: attr()`: el contenido
                                       generado por CSS no se expone de forma
                                       fiable a un lector de pantalla, y para
                                       esconder el de verdad habria que taparlo
                                       con un apaño. Asi el dato es uno solo y
                                       lo lee todo el mundo. */
                                    <span className="podio" data-puesto={st.placing}>
                                      {st.placing}
                                    </span>
                                  ) : (
                                    (st.placing ?? '—')
                                  )}
                                </td>
                                <td data-label={t('tourPlayer')}>
                                  {/*
                                    La bandera sale del codigo de pais de dos
                                    letras: cada letra se traslada a su
                                    indicador regional Unicode. Sin pais, nada.
                                  */}
                                  {st.country && /^[A-Za-z]{2}$/.test(st.country) && (
                                    <Bandera cc={st.country} />
                                  )}
                                  {/* El nombre abre su perfil: los standings
                                      ya saben quien es cada uno, y un nombre
                                      con historia vale mas que uno suelto. */}
                                  <button
                                    type="button"
                                    className="tour__jugador"
                                    onClick={() => setPerfil(st.player)}
                                  >
                                    {st.name}
                                  </button>
                                </td>
                                <td className="num" data-label={t('tourRecord')}>
                                  {st.wins}-{st.losses}{st.ties ? `-${st.ties}` : ''}
                                </td>
                                <td data-label={t('tourDeck')}>
                                  {/* Nombre arriba y nucleo en sprites debajo,
                                      el patron de la casa para "esta fila es
                                      un mazo" (NucleoSprites.jsx) — el mismo
                                      de la tabla de arquetipos. */}
                                  <span className="meta__arqceld">
                                    <span className="meta__arqnombre">
                                      {st.deckName ?? <span className="muted">—</span>}
                                    </span>
                                    {st.nucleo?.length > 0 && <Nucleo cartas={st.nucleo} t={t} sinCuenta />}
                                  </span>
                                </td>
                                <td className="tour__accion">
                                  {st.hasList && (
                                    <button
                                      type="button"
                                      /* La etiqueta NO cambia a "Cargando…":
                                         el ancho del boton variaba y toda la
                                         columna se corria. El estado ocupado
                                         se dice atenuando (is-busy), con el
                                         mismo texto y el mismo ancho. */
                                      className={`btn btn--ghost tour__ver${listaBusy === st.player ? ' is-busy' : ''}`}
                                      disabled={listaBusy != null}
                                      aria-busy={listaBusy === st.player}
                                      /* stopPropagation: la fila entera abre
                                         el perfil, y este boton abre la LISTA
                                         -- sin el freno haria las dos cosas. */
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        abrirListaDeTorneo(tor.id, st);
                                      }}
                                    >
                                      {t('tourViewList')}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        )}
                        {vistaTour === 'standings' && tourDetail.standings.length >= 32 && (
                          <p className="muted tour__pie">
                            {t('tourTop', { n: tourDetail.standings.length, total: tor.players })}
                          </p>
                        )}
                        {tor.decklists === 0 && (
                          <p className="muted tour__pie">{t('tourNoLists')}</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <Paginador
          pagina={Math.min(tourPagina, Math.max(1, Math.ceil(tourneys.tournaments.length / POR_PAGINA)))}
          total={Math.max(1, Math.ceil(tourneys.tournaments.length / POR_PAGINA))}
          onPagina={setTourPagina}
        />
        </>
      ))}

      {pane === 'players' && (fallos.players && !playersRank ? (
        carteFallo('players')
      ) : !playersRank ? (
        <Cargando texto={t('loading')} />
      ) : (playersRank.players ?? []).length === 0 ? (
        <Vacio tipo="pokebola" texto={t('metaNoData')} />
      ) : (
        <>
        <p className="muted meta__comoLeer">{t('playersHint')}</p>
        {/* El buscador vive FUERA del "sin resultados", como el de arquetipos:
            si una busqueda deja cero filas, el campo sigue ahi para corregirla. */}
        <div className="meta__buscarfila">
          <input
            className="meta__buscar"
            type="search"
            value={busquedaJugador}
            onChange={(e) => setBusquedaJugador(e.target.value)}
            placeholder={t('searchPlayer')}
            aria-label={t('searchPlayer')}
          />
          <p className="muted meta__shown">
            {t('playersShown', { n: playersOrdenados.length, total: playersRank.players.length })}
          </p>
        </div>
        {/* Orden para movil, como en el ranking de arquetipos: alli la tabla
            se vuelve tarjetas y las cabeceras clicables dejan de existir. */}
        <div className="meta__sortbar" role="group" aria-label={t('sortBy')}>
          <span className="meta__sortbarLabel">{t('sortBy')}</span>
          <div className="meta__sortchips">
            {PLAYER_COLS.filter((c) => c.key !== 'name').map((col) => {
              const active = playersSort?.key === col.key;
              return (
                <button
                  key={col.key}
                  type="button"
                  className={`meta__chip${active ? ' is-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => togglePlayersSort(col)}
                >
                  {col.labelKey ? t(col.labelKey) : col.label}
                  {active && (
                    <span className="meta__chipdir" aria-hidden="true">
                      {playersSort.dir === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {playersOrdenados.length === 0 ? (
          <p className="muted center">{t('playersSearchEmpty', { q: busquedaJugador.trim() })}</p>
        ) : (
        <>
        <table className="meta__table">
          <thead>
            <tr>
              <th scope="col" className="meta__rankh">{t('rank')}</th>
              {PLAYER_COLS.map((col) => {
                const active = playersSort?.key === col.key;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={col.align === 'num' ? 'num' : undefined}
                    aria-sort={active ? (playersSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button
                      type="button"
                      className={`meta__sortbtn${active ? ' is-active' : ''}`}
                      onClick={() => togglePlayersSort(col)}
                      aria-label={`${t('sortBy')} ${col.labelKey ? t(col.labelKey) : col.label}`}
                    >
                      <span>{col.labelKey ? t(col.labelKey) : col.label}</span>
                      <svg className="meta__sortarrow" viewBox="0 0 10 6" aria-hidden="true">
                        <path d="M1 1.5 5 5 9 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                      </svg>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {playersOrdenados
              .slice((playersPagina - 1) * POR_PAGINA, playersPagina * POR_PAGINA)
              .map((p, i) => (
                /* La fila entera abre el perfil: apuntar al nombre era pedir
                   punteria sin motivo. El boton del nombre queda como camino
                   de teclado. */
                <tr key={p.player} className="jrow" onClick={() => setPerfil(p.player)}>
                  <td className="meta__rank">{(playersPagina - 1) * POR_PAGINA + i + 1}</td>
                  <td data-label={t('tourPlayer')}>
                    {p.country && /^[A-Za-z]{2}$/.test(p.country) && (
                      <Bandera cc={p.country} />
                    )}
                    <button type="button" className="tour__jugador" onClick={() => setPerfil(p.player)}>
                      {p.name}
                    </button>
                  </td>
                  <td className="num" data-label={t('playersTournaments')}>{p.tournaments}</td>
                  <td className="num" data-label={t('tourRecord')}>
                    {p.wins}-{p.losses}{p.ties ? `-${p.ties}` : ''}
                  </td>
                  <td className="num" data-label="Top 8">{p.top8}</td>
                  <td className="num" data-label={t('playersFirsts')}>{p.firsts}</td>
                  <td className="num" data-label={t('winRateShort')}>
                    {p.winRate == null ? '—' : `${Math.round(p.winRate * 100)}%`}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <Paginador
          pagina={Math.min(playersPagina, Math.max(1, Math.ceil(playersOrdenados.length / POR_PAGINA)))}
          total={Math.max(1, Math.ceil(playersOrdenados.length / POR_PAGINA))}
          onPagina={setPlayersPagina}
        />
        </>
        )}
        </>
      ))}

      {pane === 'matchups' && matrixLocked && (
        /*
          TEASER de la matriz para el plan gratis: una mini-matriz ILUSTRATIVA
          difuminada con el candado encima, como los demas teasers (lista de
          partidas, estadisticas del mazo). Antes era una barra de texto que no
          enseñaba que aspecto tiene lo que se esta perdiendo. Los numeros son
          inventados y plausibles; los datos reales nunca llegan al cliente en
          este plan (los retiene el servidor).
        */
        <div className="proTeaser">
          <div className="proTeaser__fondo" aria-hidden="true" inert>
            <div className="meta__scroll">
              <table className="meta__matrix">
                <thead>
                  <tr>
                    <th />
                    {MATRIZ_MUESTRA.map((f) => <th key={f.nombre}>{f.nombre}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {MATRIZ_MUESTRA.map((fila) => (
                    <tr key={fila.nombre}>
                      <th>{fila.nombre}</th>
                      {fila.celdas.map((v, i) =>
                        v == null ? (
                          <td key={i}>—</td>
                        ) : (
                          <td key={i} style={cellStyle(v)}>{Math.round(v * 100)}%</td>
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <button
            type="button"
            className="matches__proLista proTeaser__cta"
            onClick={() => setGate({ feature: 'matrix' })}
          >
            <span className="matches__proLock" aria-hidden="true"><Candado /></span>
            <span className="matches__proTexto">
              <strong>{t('gateMatrixTitle')}</strong>
              <span className="muted">{t('gateMatrixBody')}</span>
            </span>
            <span className="matches__proCta">{user ? t('seeAllPro') : t('seeAllProTrial')}</span>
          </button>
        </div>
      )}

      {/*
        La matriz era la unica pestaña que podia quedarse EN BLANCO: se pintaba
        solo si habia filas, asi que mientras cargaba -- o si la peticion se
        caia -- no habia pokebola, ni aviso, ni nada. Una pestaña que no
        contesta se lee como una pestaña rota.
      */}
      {pane === 'matchups' && !matrixLocked && !matrix?.matrix?.length && (
        fallos.matrix ? (
          carteFallo('matrix')
        ) : !matrix ? (
          <Cargando texto={t('loading')} />
        ) : (
          <Vacio tipo="pokebola" texto={t('metaNoData')} />
        )
      )}

      {pane === 'matchups' && !matrixLocked && matrix?.matrix?.length > 0 && (
        <section className="meta__matrixwrap">
          {/* h2 por lo mismo que el titulo del detalle: bajo el h1 no hay
              ningun otro nivel en esta pantalla. */}
          <h2>
            {t('matchups')}
            <span className="muted"> · {t('minGames')} {matrix.minGames}</span>
          </h2>
          {/*
            COMO SE LEE, dicho antes de la tabla y no en letra pequeña despues:
            la confusion tipica es ver 60 % en A-contra-B y 40 % en B-contra-A y
            creer que son datos distintos -- son la MISMA serie de partidas
            contada desde cada lado (no suman 100 exacto por los empates).
          */}
          {/*
            LAS DOS FORMAS SE PINTAN SIEMPRE y el CSS elige: la tabla cruzada
            en escritorio, la lista por mazo en el telefono. Mismo dato, misma
            fuente, ningun estado duplicado que se pueda desincronizar -- es lo
            mismo que ya hacen los chips de orden de los arquetipos.
          */}
          <MatrizMovil
            matrix={matrix}
            mio={mxMio}
            onMio={setMxMio}
            orden={mxOrden}
            onOrden={setMxOrden}
            t={t}
            lang={lang}
          />

          {/* El "como se lee" y la leyenda son de la TABLA: hablan de celdas
              espejo y de escala de color, dos cosas que en la lista no
              existen. Van dentro del bloque que solo se ve en escritorio. */}
          <div className="meta__matrixtabla">
          <p className="muted meta__comoLeer">{t('matrixHowTo')}</p>
          <div className="meta__scroll">
            <table className="meta__matrix" onMouseLeave={() => setCruz(null)}>
              <thead>
                <tr>
                  {/* La esquina de una tabla cruzada estaba VACIA, y axe lo
                      marca con razon: es la unica celda que puede decir que
                      son las filas y que son las columnas. Sin pintar nada
                      -- ahi no cabe texto -- pero dicho para quien la recorre
                      con un lector, que es justo quien no ve que la tabla es
                      cruzada. */}
                  <th scope="col">
                    <span className="sr-only">{t('matrixCorner')}</span>
                  </th>
                  {/* Columnas con CARAS, no con texto rotado: el nombre en
                      vertical a 92 px era lo que hacia ilegible la matriz de
                      un vistazo. Dos sprites identifican el arquetipo (las
                      filas enseñan el mapeo nombre-caras); el nombre sigue en
                      title y para lectores. El cajon `other` no tiene nucleo
                      a proposito y conserva su rotulo en texto. */}
                  {matrix.matrix.map((m) => {
                    const nuc = matrix.nucleos?.[m.id];
                    return (
                      <th
                        key={m.id}
                        title={m.name}
                        className={cruz?.col === m.id ? 'is-cruz' : ''}
                      >
                        {nuc?.length > 0 ? (
                          <>
                            <span className="mx__col" aria-hidden="true">
                              {nuc.slice(0, 2).map((c) => (
                                <Mini key={c.cardId} carta={{ ...c, count: 1 }} />
                              ))}
                            </span>
                            <span className="sr-only">{m.name}</span>
                          </>
                        ) : m.name}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {matrix.matrix.map((row) => (
                  <tr key={row.id}>
                    <th title={row.name} className={cruz?.fila === row.id ? 'is-cruz' : ''}>
                      <span className="mx__fila">
                        <span className="mx__nombre">{row.name}</span>
                        {matrix.nucleos?.[row.id]?.length > 0 && (
                          <span className="mx__minis" aria-hidden="true">
                            {matrix.nucleos[row.id].slice(0, 4).map((c) => (
                              <Mini key={c.cardId} carta={{ ...c, count: 1 }} />
                            ))}
                          </span>
                        )}
                      </span>
                    </th>
                    {row.vs.map((c) => {
                      const colFila = matrix.matrix.find((m) => m.id === c.id);
                      const colName = colFila?.name ?? c.id;
                      const enCruz = cruz && (cruz.fila === row.id || cruz.col === c.id);
                      /*
                       * LA MISMA FRASE DESDE LOS DOS LADOS. Una celda y su
                       * espejo cuentan la misma serie de partidas, pero cada
                       * una la contaba desde su lado y las dos frases parecian
                       * datos contradictorios ("gana el 60 %" aqui, "gana el
                       * 40 %" alla). Ahora ambas celdas dicen EXACTAMENTE lo
                       * mismo: los dos porcentajes juntos, en el orden de la
                       * tabla, sobre las mismas N partidas. Mismo cruce, misma
                       * frase: no queda nada que "no cuadre".
                       */
                      const espejo = colFila?.vs.find((x) => x.id === row.id);
                      const idxFila = matrix.matrix.findIndex((m) => m.id === row.id);
                      const idxCol = matrix.matrix.findIndex((m) => m.id === c.id);
                      const [pn, ps] = idxFila <= idxCol
                        ? [{ n: row.name, w: c.winRate }, { n: colName, w: espejo?.winRate }]
                        : [{ n: colName, w: espejo?.winRate }, { n: row.name, w: c.winRate }];
                      const frase = c.winRate == null
                        ? t('matrixNoSample', { a: row.name, b: colName, n: c.games })
                        : t('matrixPairTip', {
                            a: pn.n, pa: pn.w == null ? '?' : Math.round(pn.w * 100),
                            b: ps.n, pb: ps.w == null ? '?' : Math.round(ps.w * 100),
                            n: c.games,
                          });
                      // La celda espejo tambien se enciende: son el mismo dato.
                      const esEspejo = cruz && cruz.fila === c.id && cruz.col === row.id;
                      return (
                        <td
                          key={c.id}
                          style={c.mirror ? {} : cellStyle(c.winRate)}
                          /* `is-sinmuestra` marca el tercer estado. Sin ella,
                             una celda sin datos era un punto sobre el fondo de
                             siempre: entre celdas pintadas de rojo y verde, la
                             ausencia de color se lee como "esta a medias", que
                             es justo lo contrario de lo que pasa -- no hay
                             dato. La trama diagonal dice "aqui no hay tabla"
                             sin ocupar un pixel mas. */
                          className={`${enCruz || esEspejo ? 'is-cruz' : ''}${cruz && !enCruz && !esEspejo ? ' is-fuera' : ''}${esEspejo ? ' is-espejo' : ''}${!c.mirror && c.winRate == null ? ' is-sinmuestra' : ''}`}
                          onMouseEnter={() => setCruz({ fila: row.id, col: c.id })}
                          title={c.mirror ? '' : frase}
                        >
                          {c.mirror ? '—' : c.winRate == null ? '·' : pct(c.winRate, 0)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted meta__legend">{t('matchupLegend')}</p>
          </div>
        </section>
      )}

      {perfil && (
        <PlayerCard
          player={perfil}
          onClose={() => setPerfil(null)}
          onOpenList={(r) =>
            abrirListaDeTorneo(r.tournamentId, {
              player: perfil,
              deckId: r.deckId,
              deckName: r.deckName,
              name: r.deckName,
            })
          }
        />
      )}

      <ProGate open={!!gate} onClose={() => setGate(null)} {...(gate ?? {})} />

      {sheet && (
        <DeckSheet
          deck={sheet.deck}
          inclusion={sheet.inclusion}
          archetypeName={sheet.name}
          onOpenCard={onOpenCard}
          onClose={() => setSheet(null)}
          /*
            Cambiar de lista SIN cerrar la hoja: se repide la misma ruta con el
            par jugador+torneo elegido y se sustituye solo el mazo (el agregado
            de inclusion es del arquetipo, no de la lista, y no cambia).
          */
          onPickList={async (opt) => {
            const r = await api(
              `/api/meta/archetypes/${encodeURIComponent(sheet.deck.deckId)}/decklist` +
                `?days=${days}&format=${format}&lang=${lang}` +
                `&player=${encodeURIComponent(opt.player)}&tournament=${encodeURIComponent(opt.tournamentId)}`
            );
            if (!r.ok) return;
            const d = await r.json();
            setSheet((s) => (s ? { ...s, deck: d } : s));
          }}
        />
      )}
    </div>
  );
}
